import { interpretUtterance, resolvePhrases } from "../../core/service";
import { fetchCandidates, fetchCascadedCounts } from "./catalog";
import {
    collectDynamicSuggestionMetas,
    fetchDynamicOptionCounts,
    isGetListCountable,
    isGetListRecountable,
    metaByGlf,
    recountableMetaByGlf,
    withProjectScope
} from "./dynamicOptions";
import { emitClientSpans, makeSpan, nowMs } from "../../core/telemetry";
import { recordAppliedState } from "../../core/decisionLog";
import { parseRelativeYears } from "../../core/parseRelativeYears";
import { handlerForKind } from "../../registry";
import { buildCascade } from "./feasibility";
import {
    detectFieldMentions,
    valueConflicts,
    buildBrowseResult,
    buildThresholdResult,
    buildPromptWildcard,
    BROWSE_REF_PREFIX
} from "./fieldMention";
import { removalMessage } from "./presenter";
import { isRecordScalarFilter } from "./applyMapping";
import { detectCrossCapabilityAmbiguity } from "../crossCapabilityClarify/crossCapabilityCatalog";
import { detectPageChoice, buildPageChoiceProposal } from "../pageChoice/pageChoice";
import { buildCapabilityClarifyProposal } from "../crossCapabilityClarify/crossCapabilityProposal";
import { buildCapabilityRedirectProposal, buildDataRedirectProposal } from "../capabilityRedirect/capabilityRedirect";

// Re-export: buildCascade moved to feasibility.js (the multi-select feasibility check needs it without
// pulling in this whole module), but it remains part of the orchestrator's public API for the guard
// below + existing tests.
export { buildCascade };

// Shown when the message is a greeting / chit-chat (or otherwise not a filter request) and the
// backend didn't supply its own assistant_message. Mirrors the backend's smalltalk guidance.
export const SMALLTALK_FALLBACK_MESSAGE =
    "No filters applied. Tell me what you'd like to filter or view — e.g. “high criticality HVAC over $50k for 2025–2028”.";

// The backend's own message for a turn, wherever the /interpret envelope carries it. For smalltalk /
// unsupported it sits on the FilterIntent (`intent.assistant_message`); for a ROUTED capability this
// bundle has no handler for (a newer backend's intent_kind reaching an older frontend — the platform_qa
// deploy-skew class, where the QA answer was silently replaced by the canned fallback), it lives on that
// capability's payload (qa / chart / navigate / table / plan — every one carries an assistant_message;
// qa's `answer` is its fuller text). Only when the response truly carries NO message should the caller
// fall back to SMALLTALK_FALLBACK_MESSAGE — never in place of a real LLM-produced reply.
export const backendAssistantMessage = interpretResponse => {
    const r = interpretResponse || {};
    const candidates = [
        r.intent && r.intent.assistant_message,
        r.qa && (r.qa.assistant_message || r.qa.answer),
        r.chart && r.chart.assistant_message,
        r.navigate && r.navigate.assistant_message,
        r.table && r.table.assistant_message,
        r.plan && r.plan.assistant_message,
        // A bare-intent response shape (no envelope) carries it at the top level.
        r.assistant_message
    ];
    const found = candidates.find(m => typeof m === "string" && m.trim());
    return found ? found.trim() : "";
};

// Concept notes: the backend attaches short QA-KB definitions of the concepts a filter turn touches
// (interpretResponse.concept_notes — e.g. what FCI means when the user filters by FCI). One per concept
// per SESSION: informative the first time, noise on every repeat — so picks are remembered module-wide.
const shownConceptNoteKeys = new Set();
export const resetShownConceptNotes = () => shownConceptNoteKeys.clear();

// The first not-yet-shown note's text for this card (marked shown on pick), or "" when there's nothing
// new to say. Wording enrichment only — it never affects which filters the card proposes.
export const pickConceptNote = interpretResponse => {
    const notes = (interpretResponse && interpretResponse.concept_notes) || [];
    for (const n of notes) {
        if (!n || !n.note || !n.key || shownConceptNoteKeys.has(n.key)) continue;
        shownConceptNoteKeys.add(n.key);
        return n.note;
    }
    return "";
};

// Client-generated idempotency key for one query. Sent with /interpret so a retried POST (lost ack on a
// flaky link) reuses the SAME id — the backend dedupes the turn by request_id, so one query can't become
// two identical traces. crypto.randomUUID where available, with a cheap fallback for older runtimes.
export const genRequestId = () => {
    try {
        if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
    } catch (e) {
        /* fall through to the manual id */
    }
    return `req-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

// Build quick lookup maps from the GET /schema payload.
export const buildSchemaMaps = schema => {
    const dimMap = {};
    (schema && schema.common_dimensions ? schema.common_dimensions : []).forEach(d => {
        dimMap[d.id] = d;
    });
    const wildcardMap = {};
    (schema && schema.wildcard_fields ? schema.wildcard_fields : []).forEach(w => {
        wildcardMap[w.id] = w;
    });
    const scalarMap = {};
    (schema && schema.scalar_toggles ? schema.scalar_toggles : []).forEach(s => {
        scalarMap[s.key] = s;
    });
    return { dimMap, wildcardMap, scalarMap };
};

// Year is handled via year_range, not as a dimension — keep it out of the dynamic field set.
const isYearField = getListKey => getListKey && getListKey.length === 1 && getListKey[0] === "maintenance_years.year";

// Boolean (Yes/No) dimensions: naming the concept ("immediate") means the POSITIVE value; resolve against
// "yes" unless the phrase is negated. Cheap negation detection — the model rarely carries it into the value
// phrase, so this mainly guards explicit "not <field>" wording.
const booleanValuePhrase = phrase =>
    /\b(no|not|non|without|exclude|excluding|isn'?t|aren'?t)\b/i.test(String(phrase || "")) ? "no" : "yes";

// Dynamic schema: the filters the dashboard actually exposes to THIS user/project, sent to interpret so
// the model only considers what the user can really filter (incl runtime / Priority Band / role-specific
// fields). `key` is what the model emits back; we map by that same key on the way out (self-consistent).
//
// ROLE-HIDDEN FIELDS ARE EXCLUDED HERE, which is what makes the sentence above true rather than aspirational.
// `hide` is the filter window's own per-role gate (today: Clients, My QA and QA Passed, all hidden from a
// client_user). It used to be honored in exactly ONE place — applyMapping.selectScalarOption — which is
// reached only for `review_status`/`qa_passed`, so the two QA wildcards were unapplyable but still offered,
// and `Clients`, a DIMENSION, was neither: Otto would happily offer AND apply a filter the user cannot see
// on their own screen. Excluding at the source fixes every consumer at once (what the model is told exists,
// what the cross-capability seam counts as filterable, what any offer path can name) instead of asking each
// new one to remember the flag.
//
// Deliberately NOT applied to the resolve/apply MAPS: those also back the applied-filter SUMMARY, which must
// still be able to describe a filter that is already in effect. Hiding a control is not the same as
// pretending an active filter isn't there — the user should be told what their data is narrowed to either
// way. The apply-side guard stays as defense in depth.
export const buildAvailableFields = (filterFields = []) =>
    (filterFields || [])
        .map(f => {
            const key = f.key || f.paramKey;
            if (!key || f.hide) return null;
            if (f.isWildCardFilter) {
                return { key, label: f.label, kind: "wildcard", value_type: f.fieldType || "text" };
            }
            if (f.getListKey && f.getListKey.length && !isYearField(f.getListKey)) {
                return {
                    key,
                    label: f.label,
                    kind: "dimension",
                    get_list_field: f.getListKey,
                    value_kind: f.getListKey.length >= 2 ? "id" : "value"
                };
            }
            return null;
        })
        .filter(Boolean);

// Add dashboard filterFields (incl dynamic ones the static /schema doesn't have) into the resolve/apply
// maps, keyed by the same `key` the model emits. Existing /schema entries are kept (richer); new dynamic
// fields are added so they resolve + apply like any other.
// SHAPE COLLISION. The static /schema is the RECOMMENDATIONS dashboard's vocabulary; a page that supplies its
// own filterFields may declare the same KEY with the other SHAPE. `criticality` is the case: a value dimension
// on the dashboard (criticalities.id|criticalities.name) and a free-text WILDCARD on the asset pages
// (assets.criticality). Merging only ever ADDED, so the loser's entry survived in the other map — and
// `metaForCandidateField` prefers dimMap, so on the Asset Dashboard Otto probed the recommendations dimension:
// the project catalog view (no asset vocabulary, and no project on a client-scoped page) returned 0 candidates,
// the resolver starved, and had it matched, `buildAssetFilterParams` would have dropped the apply as "no field
// config" — Otto says Applied, nothing changes (trace 24e7f9b4 / FTO-87).
//
// So: for a key the page DECLARES, the page's shape wins and the stale entry in the other map is removed. This
// is not a preference, it's consistency — `buildAvailableFields` derives what the model is told exists from
// these same page fields, so resolve/apply must speak about the field the same way. A key the page doesn't
// declare is untouched (the dashboard's own fields mirror the schema, so this is a no-op there).
const dropCollidingShape = (map, key, declaredHere) => {
    if (declaredHere && map[key]) delete map[key];
};

export const augmentMapsWithFilterFields = (maps, filterFields = []) => {
    const dimMap = { ...maps.dimMap };
    const wildcardMap = { ...maps.wildcardMap };
    (filterFields || []).forEach(f => {
        const key = f.key || f.paramKey;
        if (!key) return;
        if (f.isWildCardFilter) {
            dropCollidingShape(dimMap, key, true);
            if (!wildcardMap[key]) wildcardMap[key] = { id: key, label: f.label, value_type: f.fieldType || "text" };
            // The dashboard's own param key + inline dropdown OPTIONS ride along on every wildcard entry
            // (merged, so a field present in BOTH the static /schema and the dashboard gets them too). This is
            // what lets a SELECT filter named as a scalar toggle ("My QA: pending") be validated against the
            // live options and labeled in the dashboard's words — see applyMapping's select-scalar path.
            wildcardMap[key] = {
                ...wildcardMap[key],
                paramKey: f.wildcardParamKey || f.paramKey,
                fieldType: f.fieldType,
                // The filter window's per-role gate rides along so the card and the apply layer agree about
                // a field this user can't actually see (see applyMapping.selectScalarOption).
                hide: f.hide,
                ...(f.optionSource ? { optionSource: f.optionSource } : {}),
                ...(f.assetGetListKey ? { assetGetListKey: f.assetGetListKey } : {}),
                ...(f.options ? { options: f.options } : {}),
                ...(f.inlineOptions ? { inlineOptions: f.inlineOptions } : {})
            };
        } else if (f.getListKey && f.getListKey.length && !isYearField(f.getListKey)) {
            dropCollidingShape(wildcardMap, key, true);
            // MERGE (not just add-if-absent): a field present in BOTH the static /schema and the dashboard
            // (e.g. `criticality`) gets a schema meta that LACKS the dashboard's routing flags
            // (isRecommendationField / searchKey). Those flags decide which option endpoint the catalog
            // fetch hits and with which searchKey — exactly how the dashboard's own getMasterFilter routes.
            // Without merging, `criticality` used the schema meta -> common /get_list + derived searchKey ->
            // ~1 option. For matching keys the schema get_list_field == the dashboard getListKey, so this
            // override is a no-op there and only ADDS the routing info.
            const existing = dimMap[key] || {};
            dimMap[key] = {
                ...existing,
                id: key,
                label: existing.label || f.label,
                get_list_field: f.getListKey,
                value_kind: f.getListKey.length >= 2 ? "id" : "value",
                // Carried for the same reason the wildcard branch carries it: a DIMENSION can be role-hidden
                // too (Clients, from a client_user). The map keeps the field so an already-applied filter can
                // still be described, but any path that ACTS on it can now see the gate.
                hide: f.hide,
                // The page-supplied option SOURCE and its endpoint key. Both must survive into the meta:
                // catalog.fetchCandidates routes on `optionSource` per FIELD, and it only ever sees this
                // map — never the original filterFields. Dropping them here starved every asset lookup
                // (trace 232e8944: 242 candidate fields, 242 starved) because the metas silently fell back
                // to the project catalog view, which carries no asset vocabulary.
                ...(f.optionSource ? { optionSource: f.optionSource } : {}),
                ...(f.assetGetListKey ? { assetGetListKey: f.assetGetListKey } : {}),
                paramKey: f.paramKey,
                isRecommendationField: f.isRecommendationField,
                isRecommendationTable: f.isRecommendationTable,
                searchKey: f.searchKey,
                isBoolean: f.isBoolean,
                // The dashboard paramKey + dynamic flag: what dynamicOptions needs to mirror the
                // FilterModal's own /get_list call (real per-option counts for dynamic fields).
                paramKey: f.paramKey,
                isDynamicField: f.isDynamicField,
                // Dynamic select/boolean fields carry their options inline (no /get_list catalog) — keep
                // them so the resolver matches against them directly (see buildProposal).
                inlineOptions: f.inlineOptions
            };
        }
    });
    return { ...maps, dimMap, wildcardMap };
};

const fieldEntryForId = (meta, candidates) => ({
    field: meta.id,
    value_kind: meta.value_kind || "id",
    candidates: candidates || []
});

const textFieldEntry = fieldId => ({ field: fieldId, value_kind: "text", candidates: [] });

export const metaForCandidateField = (id, maps) => {
    if (maps.dimMap[id]) return maps.dimMap[id];
    if (maps.wildcardMap[id]) {
        // Only free-text wildcard fields are valid clarify options; numeric/date fields need an
        // operator + value, so offering the phrase as their text value is meaningless.
        return maps.wildcardMap[id].value_type === "text" ? { id, value_kind: "text" } : null;
    }
    if (id === "search") return { id: "search", value_kind: "text" };
    return null;
};

// Scope-paired dimensions: a building-level field and its site-level twin (FCI/EFCI "Description") share
// an IDENTICAL value vocabulary + synonyms, so the resolver ALWAYS ties them on embedding score (e.g.
// "worst" -> "Critical" scores the same for building_fci and site_fci). Probing both therefore never adds
// resolver signal — it only yields two indistinguishable suggestions and buries the scope the user asked
// for. The classifier already encodes that scope in candidate_dimensions ("worst BUILDINGS" -> building_*,
// not site_*). So when it hinted one twin but not the other, we honor that and don't let the baseline
// re-inject the un-hinted twin. Deliberately narrow to this identical-twin family: unpaired fields and
// genuinely-distinct pairs (building/site country, state) stay fully probed (no starvation regression —
// see [[resolver-candidate-starvation]]). When NEITHER twin is hinted (no scope word), both are probed as
// before and the clarify card disambiguates them by their labels.
//
// This prune only works if the hint set still REFLECTS the model's scope choice. The backend's gloss-overlap
// candidate expansion (`interpreter._expand_label_gloss_overlap`) runs first and appends to the very same
// `candidate_dimensions`, so for five weeks it added the site twins itself — they arrived here looking
// hinted, nothing was excluded, and "worst buildings" offered `Critical` four times (FTO-9). The backend now
// declines to add the other scope of a vocabulary already on the list; the `xrepo-scope-twins` gate keeps
// the two sides from drifting apart again. A new pair here needs the backend's `_SCOPE_PREFIXES` to cover it.
const SCOPE_TWIN = {
    building_fci: "site_fci",
    site_fci: "building_fci",
    building_efci: "site_efci",
    site_efci: "building_efci"
};

// Candidate FIELDS to probe for an ambiguous term — the model's hints PLUS a DATA-DRIVEN baseline read
// live from the schema (maps.dimMap), so DATA decides the field, not prompt rules or a hand-kept list.
//
// We deliberately do NOT maintain a static "fields to probe" list: filters are added/removed on the
// frontend every release (priority-band elements like "Compliance", dynamic selects, project-specific
// dimensions), and a hardcoded list silently goes stale — the field is "right there" on the dashboard yet
// never becomes a candidate, so the resolver never gets to score it (see [[resolver-candidate-starvation]]).
// Instead, every controlled-vocabulary dimension currently exposed to this user/project is probed and the
// catalog + resolver decide which actually contains a match.
//
// In: controlled-vocabulary dims (isControlledVocabulary — value_kind "value", incl. priority/dynamic/
// project-specific fields, plus bounded id-vocabularies like category/trade/system/region). Out: free-text
// fields (description/notes/search — a "contains" of a GUESSED phrase usually matches nothing, so offering
// it is misleading) and high-cardinality id dims (site/building/floor/manufacturer/asset…), too large to
// full-fetch for every term; those stay reachable only when the model explicitly names them (hinted); and
// a scope-paired twin whose OTHER scope the model hinted but this one it did not (see SCOPE_TWIN above).
export const buildAmbiguousMetas = (at, maps, offerable) => {
    const hintedIds = new Set((at && at.candidate_dimensions) || []);
    // Fields the PAGE can actually offer (buildAvailableFields' key set). `maps` is always the filter-ai
    // /schema vocabulary with the page's own fields merged on top, so on any page whose fields are NOT the
    // recommendations set, dimMap is mostly dimensions this page can neither offer nor apply. Probing them
    // is pure waste — cheap on the dashboard (catalog cache hit), but one HTTP request each on a page with
    // its own option source: 37 of the 38 fetches behind "show me only florida" on the Asset Dashboard were
    // recommendations dimensions that could never have matched. A hinted field is never dropped, so this
    // only prunes baseline noise; omitting `offerable` keeps the old behavior exactly.
    // An EMPTY set means "we don't know what this page offers", not "it offers nothing" — a mount that has
    // not assembled its fields yet (or a caller that passes none) must keep the full sweep rather than have
    // every candidate pruned and every phrase silently fail to resolve. Fail open, like every other
    // availability guard in Otto.
    const canOffer = offerable instanceof Set && offerable.size ? offerable : null;
    const hinted = [...hintedIds].map(id => metaForCandidateField(id, maps));
    // Derived from the live schema, so newly added/removed filters are picked up automatically with
    // nothing to maintain. isControlledVocabulary is defined below — referenced here at call time.
    // The BASELINE sweep — every controlled-vocabulary dimension — is what makes an unhinted phrase
    // reachable at all, and it is free on a page served by the project catalog view: one load, then every
    // field answers from memory. It is NOT free for a page whose options come per-field over the network
    // (`optionSource`, e.g. Asset Management's /assets/get_list, Buildings' getListForCommonFilter): there
    // it becomes one HTTP call per field.
    // Measured on the assets grid: 242 fields probed for a single phrase, which is both unusably slow and
    // pointless — the interpreter already named the plausible fields in candidate_dimensions. So a
    // page-sourced field joins the sweep only when it was HINTED; catalog-backed pages are untouched.
    const baseline = Object.values((maps && maps.dimMap) || {}).filter(
        meta =>
            isControlledVocabulary(meta) &&
            (!meta.optionSource || hintedIds.has(meta.id)) &&
            (!canOffer || canOffer.has(meta.id) || hintedIds.has(meta.id))
    );
    // Drop a scope-paired twin when the model hinted the OTHER scope but not this one — a hinted field is
    // never itself excluded (it is in hintedIds), so this only prunes baseline noise.
    const scopeExcluded = id => {
        const twin = SCOPE_TWIN[id];
        return Boolean(twin) && hintedIds.has(twin) && !hintedIds.has(id);
    };
    const byId = {};
    [...hinted, ...baseline].forEach(meta => {
        if (!meta || meta.value_kind === "text" || byId[meta.id] || scopeExcluded(meta.id)) return;
        byId[meta.id] = meta;
    });
    return Object.values(byId);
};

// Bounded controlled-vocabulary dimensions: small, enumerable option sets. For these we fetch the WHOLE
// vocabulary (no phrase-search) and let the RESOLVER match it (exact/synonym/fuzzy/embedding). The old
// path searched the catalog with the user's RAW phrase and dropped any field that returned no rows — but
// the catalog does a substring/LIKE match, so any extra/different word starved the resolver before it
// could score ("compliance issues" never substring-matches the option "Code Compliance", so Category was
// dropped and the query came back "no match"). Letting DATA + the resolver decide — not a substring
// prefilter — is the whole point of Stage B. High-cardinality id dimensions (site, building, floor,
// addition, asset...) are NOT listed here: fetching them whole is costly, so they stay search-based with
// a salient-token fallback below.
const BOUNDED_DIMENSIONS = new Set([
    "category", "trade", "system", "sub_system", "criticality", "criticality_type",
    "capital_type", "funding_source", "responsibility", "building_type", "department",
    "space_type", "region", "status", "recommendation_type", "condition",
    "water", "energy", "facility_master_plan", "infrastructure_request", "budget_priority"
]);

// Safety cap on options handed to the resolver per field (bounds the embedding cost of the probe).
// Bounded vocabularies are normally far smaller than this; the cap just guards a pathological field.
const MAX_CANDIDATES_PER_FIELD = 200;

// Content tokens for the high-cardinality fallback search — drop very short filler ("of", "a", "in").
const contentTokens = phrase =>
    String(phrase || "")
        .toLowerCase()
        .split(/\s+/)
        .filter(t => t.length >= 3);

const dedupeCandidates = options => {
    const seen = new Set();
    const out = [];
    (options || []).forEach(o => {
        const k = `${o.id}|${o.name}`;
        if (!seen.has(k)) {
            seen.add(k);
            out.push(o);
        }
    });
    return out;
};

// Display floor for suggestion pills (user rule 2026-07-23): a scored match below this doesn't earn an
// offer — weak fuzzy overlaps ("Split System" at 0.42 for "shut down my facility") read as noise and bury
// the real picks. This is a PRESENTATION cut, deliberately NOT the resolver's suggest_threshold (0.40):
// that is backend calibration gated by the resolve eval and shared by every decision path.
export const SUGGESTION_SCORE_FLOOR = 0.57;

// Fuzzy is CHARACTER overlap, not meaning, and it swings hard on phrase wording: "compliance" scored
// 0.706 on "Cemline" and 0.667 on "Complete" (session aa027e58 — the same question re-asked with history
// context extracted "compliance" instead of "compliance issues" and suddenly offered 4 pills instead of
// 1). Genuine fuzzy hits — typos, token subsets like "florida" ⊂ "Florida Panhandle" — score ≥ ~0.8, so
// fuzzy offers clear a stricter bar; the semantic tiers (embedding/synonym/exact) keep the base floor.
// This makes the offer set robust to extraction jitter: same meaning -> same pills, whatever the wording.
export const FUZZY_SCORE_FLOOR = 0.75;

// The SEMANTIC tiers (embedding / synonym / exact) get their own, LOWER floor, aligned with the resolver's
// `suggest_threshold`. A gloss match is a different word with the same meaning — it can never score high on
// character overlap, and the embedding tier is calibrated into a compressed band
// (`_calibrate_embedding` = (cosine - 0.30) / 0.25, capped at EMB_MAX 0.79), so the 0.57 base floor
// demanded cosine >= 0.4425 — ABOVE the genuine matches resolver.py itself cites as the calibration
// target: "ductwork" -> HVAC (cosine 0.43 -> 0.52) and "switchgear" -> Electrical (0.41 -> 0.44). Both were
// silently hidden, while a meaningless CHARACTER overlap at 0.80 sailed through. The backend already
// applied `suggest_threshold` when it decided these were worth offering, so for semantic matches the
// frontend must not second-guess it — the floor's job is cutting weak fuzzy noise, and that is all it does.
//
// MIRRORED with `ResolveRequest.suggest_threshold` in filter-ai/app/core/schemas/resolve.py — the two must
// stay equal, or suggestions the backend offers get silently hidden here (exactly the bug above). Guarded
// by the otto-sync `xrepo-semantic-floor` parity check; see .claude/skills/otto-sync/EXTENDING.md.
export const SEMANTIC_SCORE_FLOOR = 0.4;
const SEMANTIC_METHODS = new Set(["embedding", "synonym", "exact"]);

const isTextEntry = s => s && (s.method === "text" || s.value_kind === "text" || s.field === "search");
const sameSuggestion = (a, b) => !!a && !!b && a.field === b.field && String(a.id) === String(b.id) && a.name === b.name;
// Fuzzy is never LOOSER than FUZZY_SCORE_FLOOR; semantic is never STRICTER than SEMANTIC_SCORE_FLOOR. An
// unknown/absent method keeps the base rule.
const meetsFloor = (s, floor) => {
    const score = (s && s.score) || 0;
    // A resolver-offered within-field PEER (`ResolveMatch.peer`) bypasses the floor entirely. These are the
    // chosen field's other plausible readings of the phrase — a word-start fragment match, or a dead-heat
    // tie with the pick — and they are LOW-SCORING BY CONSTRUCTION: token scoring only pays 1.0 for a
    // whole-word hit, so "west" scores 0.727 on BOTH Midwest and Western. The 0.75 fuzzy floor would delete
    // precisely the alternative the peer set exists to surface, leaving whichever the catalog listed first
    // — the coin-toss the backend fix removes, silently reintroduced here. Same rule the semantic tiers
    // already get: the backend applied its own bar when it decided to offer these, so the display floor
    // must not second-guess it. The floor's job is cutting noise from UNRELATED fields, which is untouched.
    if (s && s.peer) return true;
    if (s && s.method === "fuzzy") return score >= Math.max(floor, FUZZY_SCORE_FLOOR);
    if (s && SEMANTIC_METHODS.has(s.method)) return score >= Math.min(floor, SEMANTIC_SCORE_FLOOR);
    return score >= floor;
};

// Drop each result's scored suggestions below their floor, in place of order. Never pruned: text-search
// entries (a "search descriptions for it" fallback affordance, not a scored match), the result's own
// `chosen` pill (its tap target must stay rendered), and any sibling OPTION of a field that already kept a
// pill — those are the resolver's deliberate within-field expansion (it applied its own
// CLARIFY_EXPAND_MARGIN to group them), and pruning one is how the correct polarity of a field Otto was
// already offering could vanish from the card: "compliance issues" scores 0.80 on "0 = No compliance issue"
// but 0.741 on "15 = Yes, there is a compliance issue", so the fuzzy floor would keep only the wrong one
// (trace f2adce44 / FTO-6). The floor exists to cut noise from UNRELATED fields, which it still does.
// A result whose scored matches ALL fall short keeps its single best so a genuine clarify never becomes a
// dead card.
export const pruneWeakSuggestions = (results, floor = SUGGESTION_SCORE_FLOOR) =>
    (results || []).map(r => {
        const suggestions = (r && r.suggestions) || [];
        if (!suggestions.length) return r;
        const scored = suggestions.filter(s => s && !isTextEntry(s));
        const strong = scored.filter(s => meetsFloor(s, floor));
        const strongFields = new Set(strong.map(s => s.field));
        const best = scored.reduce((a, b) => (a && (a.score || 0) >= ((b && b.score) || 0) ? a : b), null);
        const kept = suggestions.filter(
            s =>
                isTextEntry(s) ||
                meetsFloor(s, floor) ||
                strongFields.has(s.field) ||
                sameSuggestion(s, r.chosen) ||
                (!strong.length && s === best)
        );
        return kept.length === suggestions.length ? r : { ...r, suggestions: kept };
    });

// ---------------------------------------------------------------------------------------------------
// Stage-A ROUTING DEMOTION — stop trusting the model's field guess for UNNAMED phrases.
//
// Three traces (329117e3 Florida→Building State, 75051a38 compliance→priority element, 0442d7e9
// "fall protection"→description contains) are one failure class: the interpreter routes a phrase to a
// single committed field or a free-text wildcard on its own guess, and the DATA never gets a vote — the
// resolver either sees only the wrong field's options or is skipped entirely. The interpreter can't do
// better: it sees field LABELS only (grounding/privacy boundary), never option values, so it cannot know
// "fall protection" is a real Sub System option. The fix is where the data lives — here.
//
// Policy: an UNNAMED committed phrase / bare free-text wildcard is DEMOTED to a full data-driven probe
// (buildAmbiguousMetas: every controlled-vocab field), so the resolver's tiers decide the field; the
// model's routing survives as (a) the FIRST probed field for a committed dim, (b) a choosable text-search
// option for a wildcard, and (c) the FALLBACK when nothing grounds. Respected as explicit (no demotion):
// the field's label named in the utterance ("HVAC system", "building state Florida"), text-search framing
// ("search the description for…"), boolean concept-fields (their phrase is the field name, probing it
// across catalogs is meaningless), and add/remove refinements (adjusting an established filter).
// ---------------------------------------------------------------------------------------------------

// Wording that shows the user MEANT a free-text search — then description/notes routing is explicit.
const TEXT_SEARCH_FRAMING = /\b(descriptions?|notes?|narratives?|search(es|ing)?|mentions?|mentioning|contains?|containing|text|keywords?)\b/i;

export const labelNamedInUtterance = (label, utterance) => {
    const l = String(label || "")
        .toLowerCase()
        .trim();
    return !!l && String(utterance || "").toLowerCase().includes(l);
};

// Demote a committed common-filter phrase to a cross-field probe? (see policy block above)
export const shouldDemoteCommitted = (cf, meta, utterance) =>
    (cf.action || "set") === "set" && !meta.isBoolean && !labelNamedInUtterance(meta.label, utterance);

// A "bare" free-text wildcard: contains-on-a-text-field carrying a phrase the user did NOT frame as a
// text search. Numeric/date/range wildcards are never demoted (they need an operator, not an option).
export const isBareTextWildcard = (w, maps, utterance) => {
    if (!w || !w.field || (w.action || "set") !== "set") return false;
    if (w.operator !== "contains" || w.value == null || String(w.value).trim() === "") return false;
    const meta = maps.wildcardMap && maps.wildcardMap[w.field];
    const isText = meta ? (meta.value_type || "text") === "text" : ["description", "notes"].includes(w.field);
    return isText && !TEXT_SEARCH_FRAMING.test(String(utterance || ""));
};

// Post-resolution bookkeeping for demoted wildcards (pure; mutates the passed intent/resolution):
//   - a `w:` result that grounded NOTHING beyond its own text option is dropped, and the original
//     wildcard stays in the intent — it applies exactly as the interpreter routed it (the fallback);
//   - a `w:` result that DID ground removes its wildcard from the intent (choosing the text suggestion
//     re-creates it via buildApplyPayload's textWildcards, so the option is still one tap away).
// Also aligns `needs_clarification` with what actually resolved, so the headline speaks from the same
// options as the buttons (a demoted phrase can turn a confident intent into a genuine question).
export const applyRoutingDemotion = (intent, resolution) => {
    const results = resolution.results || [];
    const kept = [];
    const grounded = new Set();
    results.forEach(r => {
        if (typeof r.ref !== "string" || !r.ref.startsWith("w:")) {
            kept.push(r);
            return;
        }
        const hasGroundedOption = (r.suggestions || []).some(s => s.value_kind !== "text");
        if (hasGroundedOption) {
            grounded.add(parseInt(r.ref.split(":")[1], 10));
            kept.push(r);
        }
    });
    if (grounded.size) {
        intent.wildcard_filters = (intent.wildcard_filters || []).filter((w, i) => !grounded.has(i));
    }
    resolution.results = kept;
    if (kept.some(r => r.decision === "clarify" || r.multi)) intent.needs_clarification = true;
};

// ---------------------------------------------------------------------------------------------------
// CASCADED-COUNT guard — never offer (or silently auto-apply) an option that would EMPTY the dashboard.
//
// Resolution grounds against the FULL project vocabulary on purpose (candidate starvation), so with
// filters already applied, a matched option can have ZERO records under the current filter state. These
// helpers stamp each suggestion with its REAL count under the applied filters (one /filter_catalog D3
// call — the same cascade the dashboard's own dropdowns use) and then: hide zero-count options from
// clarify lists; demote a zero-count auto to an explicit confirm with a plain warning; and when EVERY
// option of a result would be empty, keep the single best as a warned confirm instead of a dead card.
// ---------------------------------------------------------------------------------------------------

const glfKey = meta => (meta && Array.isArray(meta.get_list_field) ? meta.get_list_field.join("|") : "");

const zeroResultsNote = m =>
    `Heads up: “${m.name}” has no matching results with your current filters — applying it would show an empty dashboard.`;

// Pure application of the cascaded counts to the resolution (exported for unit tests). `countsByGlf`
// comes from fetchCascadedCounts. A suggestion is only judged when its field IS covered by the counts
// (text options, "search", and uncovered fields are kept — never hide what we can't verify). Returns
// the per-ref hidden tally for the trace span.
export const applyCascadedCounts = (results, countsByGlf, maps) => {
    const hidden = [];
    const countOf = m => {
        if (!m || m.value_kind === "text" || m.field === "search") return null;
        const byOption = countsByGlf[glfKey(maps.dimMap && maps.dimMap[m.field])];
        if (!byOption) return null; // field not covered by the cascaded catalog -> unverifiable, keep
        return byOption[String(m.id !== undefined && m.id !== null ? m.id : m.name)] || 0;
    };
    const isEmptyUnderFilters = m => countOf(m) === 0;

    (results || []).forEach(r => {
        // A zero-count AUTO becomes an explicit confirm: the user asked for this specific thing, so we
        // keep it selectable but never silently empty their dashboard.
        if (r.decision === "auto" && r.chosen && isEmptyUnderFilters(r.chosen)) {
            r.decision = "confirm";
            r.zero_results_note = zeroResultsNote(r.chosen);
            return;
        }
        const before = r.suggestions || [];
        const survivors = before.filter(m => !isEmptyUnderFilters(m));
        const removed = before.length - survivors.length;
        if (!removed) return;
        hidden.push({ ref: r.ref, removed });
        if (survivors.length) {
            r.suggestions = survivors;
            if (r.chosen && isEmptyUnderFilters(r.chosen)) r.chosen = r.multi ? null : survivors[0];
        } else {
            // EVERY option would be empty — keep the single best as a warned confirm (mirrors the auto
            // guard) instead of presenting a dead, empty clarify card.
            const top = before[0];
            r.suggestions = [top];
            r.chosen = top;
            r.multi = false;
            r.decision = "confirm";
            r.zero_results_note = zeroResultsNote(top);
        }
    });
    return hidden;
};

// A controlled-vocabulary dimension = a small, enumerable option set we should hand to the resolver
// WHOLE. Decided by DATA, not just a name list: `value_kind === "value"` dimensions (status, condition,
// criticality, LOF, impact, and project-specific ones like `building_criticality`) store the value in a
// single enum column, so they're inherently small — full-fetch them regardless of their key. This is the
// fix for the recurring starvation where a value field's KEY wasn't in BOUNDED_DIMENSIONS (e.g.
// `building_criticality` not `criticality`), so "high criticality" was phrase-searched, returned ~1 row,
// and the resolver never saw {Critical, Moderate, New}. BOUNDED_DIMENSIONS still covers small *id*-based
// vocabularies (trade/system/region/...) that aren't value_kind "value".
// Exported for the field-mention gate (fieldMention.js takes it as a parameter — importing it back from
// here would be a module cycle) and for tests.
export const isControlledVocabulary = meta => (meta && meta.value_kind === "value") || BOUNDED_DIMENSIONS.has(meta.id);

// Hybrid candidate fetch (the fix for the substring-prefilter starvation described above):
//   - controlled vocabulary  -> fetch the FULL vocabulary (no search) and let the resolver match it
//   - high-cardinality dim    -> search by the phrase, then by salient tokens, before giving up
// Always returns at most MAX_CANDIDATES_PER_FIELD options. Exported for unit testing.
export async function fetchResolveCandidates(meta, phrase, scope) {
    let candidates = [];
    if (isControlledVocabulary(meta)) {
        candidates = await fetchCandidates(meta, "", scope); // full controlled vocabulary
        if (!candidates.length) candidates = await fetchCandidates(meta, phrase, scope); // safety fallback
    } else {
        candidates = await fetchCandidates(meta, phrase, scope); // narrow large catalogs by the phrase
        if (!candidates.length) {
            const tokenLists = await Promise.all(contentTokens(phrase).map(t => fetchCandidates(meta, t, scope)));
            candidates = dedupeCandidates(tokenLists.flat());
        }
    }
    return candidates.length > MAX_CANDIDATES_PER_FIELD ? candidates.slice(0, MAX_CANDIDATES_PER_FIELD) : candidates;
}

// Background prefetch fan-out width. Bounded so warming stays gentle on the backend and the browser's
// ~6-connections-per-host limit while still finishing quickly.
const WARM_CONCURRENCY = 4;

// Run `worker` over `items` with at most `limit` in flight at once.
async function runWithConcurrency(items, limit, worker) {
    const queue = items.slice();
    const runner = async () => {
        while (queue.length) {
            // eslint-disable-next-line no-await-in-loop
            await worker(queue.shift());
        }
    };
    await Promise.all(Array.from({ length: Math.min(limit, queue.length) }, runner));
}

// Background pre-fetch ("warm"): populate the candidate cache for every controlled-vocabulary dimension
// currently exposed to this user/project, so the FIRST Otto query resolves against an already-warm cache
// instead of paying the slow per-field /get_list fan-out on the critical path (the dashboard's options API
// is uncached on a cold call). Fire-and-forget — per-field failures are swallowed. Cheap to call again:
// the catalog cache + in-flight de-dup make repeats no-ops, and a re-run after the dynamic bands land just
// fills the few new fields. inlineOptions fields (dynamic select/boolean) never hit the catalog, so skip
// them; high-cardinality id dims are excluded by isControlledVocabulary (too large to full-fetch eagerly).
export async function warmCatalog({ maps, projectId, projectIds, buildingIds, candidateSource } = {}) {
    if (!maps || !maps.dimMap) return;
    const scope = { projectId, projectIds, buildingIds, candidateSource }; // buildingIds = D2 per-user permission scope
    // Page-SOURCED fields are excluded: warming exists to pre-pay ONE catalog load, but for a source that
    // costs a request per field it would fan the whole catalog out on mount — the exact cost this is meant
    // to avoid. Those fields are fetched on demand, bounded to the hinted set (see buildAmbiguousMetas).
    const metas = Object.values(maps.dimMap).filter(
        meta => isControlledVocabulary(meta) && !(meta.inlineOptions && meta.inlineOptions.length) && !meta.optionSource
    );
    await runWithConcurrency(metas, WARM_CONCURRENCY, async meta => {
        try {
            await fetchResolveCandidates(meta, "", scope);
        } catch (e) {
            /* best-effort warm — one field failing must not abort the prefetch */
        }
    });
}

// Convert the dashboard's active-filter summary into the interpret context shape. Dashboard values
// are opaque ids, so we signal which dimensions/wildcards/years are ACTIVE — enough for the model
// to reason about refinements ("remove the cost filter", "swap criticality", "also add X").
export function buildActiveContext(activeFilters, maps) {
    if (!activeFilters) return null;

    const byGetListField = {};
    Object.values((maps && maps.dimMap) || {}).forEach(d => {
        byGetListField[JSON.stringify(d.get_list_field)] = d.id;
    });

    const list = {};
    (activeFilters.list || []).forEach(item => {
        const dimId = byGetListField[JSON.stringify(item.get_list_field)] || item.label;
        if (dimId) list[dimId] = ["applied"];
    });

    const filters = {};
    (activeFilters.wildcards || []).forEach(w => {
        if (w.label) filters[w.label] = "applied";
    });

    const hasAny =
        Object.keys(list).length > 0 ||
        Object.keys(filters).length > 0 ||
        activeFilters.start_year != null ||
        activeFilters.end_year != null;
    if (!hasAny) return null;

    return {
        list,
        filters,
        scalars: {},
        search: null,
        start_year: activeFilters.start_year ?? null,
        end_year: activeFilters.end_year ?? null
    };
}

// How long we will wait for applied option NAMES before sending the request without them. The catalog is
// warmed when the widget opens and cached across turns, so this normally resolves in a microtask; the bound
// exists so a cold/slow catalog delays a turn by at most this much instead of holding the user's message.
const USER_STATE_TIMEOUT_MS = 1500;

/**
 * The USER-DECISION context: what is applied (with option names resolved) plus what the user changed since
 * their last message. Consumed by the PLANNER so it never plans a step the user already performed — see
 * core/decisionLog for why neither `active_filters` nor `history` can carry this.
 *
 * Deliberately SEPARATE from buildActiveContext rather than an enrichment of it. `active_filters` is fed
 * verbatim into the interpret prompt, which is a documented fragile equilibrium, and its value-less shape
 * is load-bearing there (given real values the model echoes the whole applied state back into its own
 * committed filters — the failure the removal path already has to defend against). So the named shape
 * travels as its own additive field and interpret stays byte-identical.
 *
 * Names come from buildActiveSummary — the SAME resolution behind "which filters are active?" and the
 * removal path — so what the planner believes is applied cannot drift from what Otto tells the user is
 * applied. Best-effort throughout: any failure or timeout degrades to labels without values, and finally
 * to omitting the field, which is exactly today's behavior.
 */
async function collectUserState(activeFilters, maps, scope) {
    const labelsOnly = () =>
        ((activeFilters && activeFilters.list) || [])
            .map(it => ({ label: it && it.label, values: [] }))
            .filter(e => e.label);

    let applied;
    try {
        const timeout = new Promise(resolve => setTimeout(() => resolve(null), USER_STATE_TIMEOUT_MS));
        const summary = await Promise.race([buildActiveSummary(activeFilters, maps, scope), timeout]);
        applied = summary
            ? summary.map(e => ({ label: e.label, values: Array.isArray(e.names) ? e.names : [] }))
            : labelsOnly();
    } catch (e) {
        applied = labelsOnly();
    }

    try {
        return recordAppliedState(applied);
    } catch (e) {
        return null; // the log is a side channel; never fail a turn for it
    }
}

// ---------------------------------------------------------------------------------------------------
// REMOVE-A-FILTER (active-set grounding) — "remove code compliance", "drop the trade filter".
//
// Removal is the mirror of adding: the only options that can be removed are the ones currently APPLIED, so
// we ground the phrase against the ACTIVE filter set (a tiny, known list from getAiActiveFilters) rather
// than the full project vocabulary. This makes "remove code compliance" land on the applied Category =
// Code Compliance (not some other Code-Compliance-ish option that isn't even on the dashboard), and lets us
// say "that filter isn't currently applied" when the target isn't there. User decisions (2026-07-05):
//   - no match  -> reply that the filter isn't applied + list the filters that ARE (buildActiveSummary);
//   - a match   -> auto-apply the removal (the card fires onApply on mount; see RemovalProposalCard).
// The intent schema already models removal (common_filters action="remove" for a value, `clears` for a
// whole field) and applyMapping already subtracts it — this only changes the GROUNDING (full vocab ->
// active set) and the interaction (auto-apply + active-summary), so it's frontend-only; the LLM evals are
// untouched.
// ---------------------------------------------------------------------------------------------------

// The applied option ids for one dimension, pulled from the dashboard's active-filter summary
// (activeFilters.list = [{ get_list_field, label, values:[ids] }]). Empty when the dimension isn't applied.
export function activeOptionIdsFor(meta, activeFilters) {
    if (!meta || !activeFilters || !Array.isArray(activeFilters.list)) return [];
    const glf = JSON.stringify(meta.get_list_field);
    const entry = activeFilters.list.find(it => JSON.stringify(it.get_list_field) === glf);
    return entry && Array.isArray(entry.values) ? entry.values.map(String) : [];
}

// The applied options {id,name} for a dimension: its full vocabulary filtered down to the applied ids, so
// we can BOTH resolve a phrase against only-applied options and name them for the summary. Best-effort:
// a failed/empty catalog fetch yields [] (nothing to remove -> "not applied"), never throws.
async function fetchActiveOptions(meta, activeFilters, scope) {
    const ids = new Set(activeOptionIdsFor(meta, activeFilters));
    if (!ids.size) return [];
    let vocab = [];
    try {
        vocab = meta.inlineOptions && meta.inlineOptions.length ? meta.inlineOptions : await fetchResolveCandidates(meta, "", scope);
    } catch (e) {
        vocab = [];
    }
    const keyOf = o => String(o.id !== undefined && o.id !== null ? o.id : o.name);
    return (vocab || []).filter(o => ids.has(keyOf(o)));
}

// The reliable signal for WHAT to remove is the user's own words matched against the ACTIVE filters — NOT
// interpret's committed filter list. Given active_filters context + a removal, the model routinely echoes
// the WHOLE current state back (restating every applied filter), so trusting its committed filters made
// "remove the HVAC filter" try to remove everything. So the removal path ignores the intent's filters
// entirely and instead intersects the utterance with the applied option names + field labels. Removing
// "everything" is then impossible unless the user literally names everything. (Grounding is preserved:
// candidates are the client's REAL applied options, fetched from the catalog.)
export const REMOVAL_VERBS = /\b(remove|removes|removing|removed|drop|drops|dropping|dropped|delete|deletes|deleting|deleted|clear|clears|clearing|cleared|unset|get rid of|take off|turn off)\b/i;
export const isRemovalUtterance = u => REMOVAL_VERBS.test(String(u || ""));

// Words that carry no target meaning in a removal phrase — stripped so what's left is the filter(s) named.
// Includes the removal verbs' leftovers, articles, "filter(s)", and reset-scope words ("all"/"everything")
// so a full reset falls through to clear_all rather than matching a random applied option.
const REMOVAL_STOPWORDS = new Set([
    "the", "a", "an", "my", "our", "your", "its", "this", "that", "these", "those", "filter", "filters",
    "filtering", "currently", "current", "active", "actively", "applied", "please", "just", "only", "for",
    "from", "off", "out", "all", "any", "also", "and", "to", "on", "of", "by", "with", "me", "i", "we",
    "want", "would", "like", "can", "you", "otto", "show", "everything", "them", "those"
]);

const tokenize = s =>
    String(s || "")
        .toLowerCase()
        .replace(/[^a-z0-9\s]/g, " ")
        .split(/\s+/)
        .filter(Boolean);

// Split a removal utterance into one SEGMENT per named target, on conjunctions/commas, so "remove the year
// and cost filters" is matched target-by-target (["year"], ["cost"]) rather than as one bag — else a short
// name like "cost" can't subset-match a longer label "Construction Cost" while the bag also holds "year".
const SEGMENT_SPLIT = /\s+(?:and|&|plus|as well as)\s+|[,;]+/i;

// Content tokens per target segment: drop the removal verbs, split into segments, then drop stopwords.
export const removalSegments = utterance =>
    String(utterance || "")
        .replace(REMOVAL_VERBS, " ")
        .split(SEGMENT_SPLIT)
        .map(seg => tokenize(seg).filter(t => !REMOVAL_STOPWORDS.has(t)))
        .filter(seg => seg.length);

// All content tokens of a removal utterance (flattened across segments) — the filter(s) the user named.
export const queryTokensFromUtterance = u => removalSegments(u).flat();

const isSubset = (a, b) => a.length > 0 && a.every(t => b.includes(t));

// Does an active candidate (an applied option NAME or a field LABEL) match ONE target segment? True when
// the candidate is fully named in the segment (candidate ⊆ segment, e.g. "code compliance") OR the segment
// is fully covered by the candidate (segment ⊆ candidate, e.g. "cost" inside "construction cost", "hvac"
// inside a long option name). Stopword tokens are dropped from the candidate so filler doesn't block a match.
export const candidateMatches = (candidateTokens, segmentTokens) => {
    const cand = (candidateTokens || []).filter(t => !REMOVAL_STOPWORDS.has(t));
    if (!cand.length || !segmentTokens || !segmentTokens.length) return false;
    return isSubset(cand, segmentTokens) || isSubset(segmentTokens, cand);
};

// A candidate matches if it matches ANY of the utterance's target segments.
const matchesAnySegment = (candidateTokens, segments) => (segments || []).some(seg => candidateMatches(candidateTokens, seg));

// A removal REQUEST: a removal-verb utterance that NAMES a specific target (non-empty query after stripping).
// Deliberately UTTERANCE-DRIVEN and independent of the intent's contents: the removal path ignores what the
// model committed and matches the utterance against the ACTIVE filters, so the model's frequent confusion on
// a removal — asking to clarify an ambiguous term ("HVAC" = system or trade?), echoing the active state, or
// mis-flagging the action — must NOT divert "remove the HVAC filter" back to the add/clarify card. Only two
// things gate it: it must classify as a filter query (belt-and-suspenders; smalltalk/chart/reset are already
// routed away before this point), and it must name something (a bare "clear all filters" has an empty query
// and falls through to the normal clear_all path).
export function isPureRemoval(intent, utterance) {
    if (!isRemovalUtterance(utterance)) return false;
    if (!queryTokensFromUtterance(utterance).length) return false; // "clear all filters" -> normal clear_all path
    if (intent && intent.intent_kind && intent.intent_kind !== "filter_query") return false;
    return true;
}

// A human summary of what IS applied (used when a requested removal isn't). One entry per applied
// dimension with its option NAMES resolved from the catalog, plus wildcard labels and the year range.
export async function buildActiveSummary(activeFilters, maps, scope) {
    if (!activeFilters) return [];
    const out = [];
    const dims = Object.values((maps && maps.dimMap) || {});
    // eslint-disable-next-line no-restricted-syntax
    for (const it of activeFilters.list || []) {
        const glf = JSON.stringify(it.get_list_field);
        const meta = dims.find(m => JSON.stringify(m.get_list_field) === glf);
        let names = [];
        if (meta) {
            // eslint-disable-next-line no-await-in-loop
            const opts = await fetchActiveOptions(meta, activeFilters, scope);
            names = opts.map(o => o.name);
        }
        out.push({ label: it.label, names });
    }
    (activeFilters.wildcards || []).forEach(w => w && w.label && out.push({ label: w.label, names: [] }));
    if (activeFilters.start_year != null || activeFilters.end_year != null) {
        out.push({ label: "Years", names: [`${activeFilters.start_year ?? "…"}–${activeFilters.end_year ?? "…"}`] });
    }
    return out;
}

/**
 * Build a REMOVAL proposal by intersecting the user's utterance with the dashboard's ACTIVE filters:
 * an applied option NAMED in the utterance ("HVAC") is a value removal; an applied field named by LABEL
 * ("system", "cost", "year") is a whole-field clear. Only what the user actually names AND is currently
 * applied is removed — nothing is inferred from interpret's committed filter list. Returns
 * { removal:true, removals, clears, clearWildcards, clearYear, notApplied, activeSummary, message }.
 * Best-effort throughout (a failed catalog fetch degrades to "not applied", never throws).
 */
export async function buildRemovalProposal({ activeFilters, maps, scope, requestId, sessionId, utterance }) {
    const segments = removalSegments(utterance);

    const removals = []; // { get_list_field, value_kind, id, name, field, label, action:"remove" }
    const clears = []; // { id, label, get_list_field }
    const clearWildcards = []; // { id, label }
    let clearYear = false;
    const dimsWithValueMatch = new Set();

    // Resolve each APPLIED dimension to its meta + applied option names (grounded from the catalog).
    const dims = Object.values((maps && maps.dimMap) || {});
    const activeDims = [];
    // eslint-disable-next-line no-restricted-syntax
    for (const it of (activeFilters && activeFilters.list) || []) {
        const meta = dims.find(m => JSON.stringify(m.get_list_field) === JSON.stringify(it.get_list_field));
        // eslint-disable-next-line no-await-in-loop
        const opts = meta ? await fetchActiveOptions(meta, activeFilters, scope) : [];
        activeDims.push({ meta, label: it.label, opts });
    }

    // 1) Value-level: the user named an APPLIED option ("HVAC" -> System = HVAC).
    activeDims.forEach(({ meta, opts }) => {
        if (!meta) return;
        opts.forEach(o => {
            if (matchesAnySegment(tokenize(o.name), segments)) {
                removals.push({
                    get_list_field: meta.get_list_field,
                    value_kind: meta.value_kind || "id",
                    id: o.id,
                    name: o.name,
                    field: meta.id,
                    label: meta.label,
                    action: "remove"
                });
                dimsWithValueMatch.add(meta.id);
            }
        });
    });

    // 2) Whole-field by LABEL ("system", "criticality") — but not when a specific value in that field
    //    already matched (the value removal is the more precise action).
    activeDims.forEach(({ meta, label }) => {
        if (!meta || dimsWithValueMatch.has(meta.id)) return;
        if (matchesAnySegment(tokenize(meta.label || label), segments)) {
            clears.push({ id: meta.id, label: meta.label || label, get_list_field: meta.get_list_field });
        }
    });

    // 3) Wildcards ("cost"): match the applied wildcard's label; its field id comes from the schema (by
    //    label) or the key the dashboard threaded through the active-filter summary.
    ((activeFilters && activeFilters.wildcards) || []).forEach(w => {
        if (!w || !w.label || !matchesAnySegment(tokenize(w.label), segments)) return;
        const wm = Object.values((maps && maps.wildcardMap) || {}).find(m => m.label && m.label === w.label);
        const fieldId = (wm && wm.id) || w.key;
        if (fieldId && !clearWildcards.some(c => c.id === fieldId)) clearWildcards.push({ id: fieldId, label: w.label });
    });

    // 4) Year ("year"/"years") — applied as year_range, not a dimension/wildcard.
    const yearApplied = activeFilters && (activeFilters.start_year != null || activeFilters.end_year != null);
    if (yearApplied && segments.some(seg => seg.includes("year") || seg.includes("years"))) clearYear = true;

    const matched = removals.length || clears.length || clearWildcards.length || clearYear;
    const activeSummary = await buildActiveSummary(activeFilters, maps, scope);
    // Nothing the user named is currently applied -> report that + list what IS (user decision).
    const notApplied = matched ? [] : [{ phrase: segments.flat().join(" ") || "that filter", label: "" }];
    const message = removalMessage({ removals, clears, clearWildcards, clearYear, notApplied, activeSummary }, maps);

    return { removal: true, removals, clears, clearWildcards, clearYear, notApplied, activeSummary, message, requestId, sessionId, utterance };
}

// The neutral apply payload for a removal — same shape buildApplyPayload emits, so applyMapping's
// remove/clear paths (removeByKey, clears, clearWildcards, clearYear) apply it unchanged.
export function buildRemovalPayload(proposal) {
    const dimensions = (proposal.removals || []).map(r => ({
        get_list_field: r.get_list_field,
        value_kind: r.value_kind || "id",
        id: r.id,
        name: r.name,
        action: "remove"
    }));
    const clears = (proposal.clears || []).map(c => ({ get_list_field: c.get_list_field }));
    // clearWildcards entries are { id, label } (label is for the message); apply only needs the field id.
    // Tolerate a bare id too, for callers/tests that pass strings.
    const clearWildcards = (proposal.clearWildcards || []).map(c => (c && c.id != null ? c.id : c));
    return { dimensions, clears, clearWildcards, clearYear: !!proposal.clearYear, wildcards: [], clear_all: false, year: null, scalars: [] };
}

// ---------------------------------------------------------------------------------------------------
// GROUNDING SPANS — the frontend half of every NON-filter capability. The backend already traces the raw
// model output (the classify span + each capability's *_interpret span); what was invisible was what the
// FRONTEND grounded that into — the decision the user actually sees (which page/chart/column, a clarify, or
// a dead end). That blind spot is exactly where the "Otto didn't know the dashboard page" bug hid: the
// backend was correct, but the empty-catalog grounding failure never reached a trace (it stopped at
// nav_interpret). These spans close it — one compact span per turn, attached to the interpret request_id,
// summarizing the built proposal so the whole decision is visible in the tracing waterfall. The
// filter_query path is already richly traced (fields.availability / catalog.fetch / resolve / cascade.count
// / year.ground, plus the dashboard's apply span), so it emits none of these. Best-effort, like all client
// spans (emitClientSpans never throws).
// ---------------------------------------------------------------------------------------------------

// Summarize a ROUTED capability proposal (navigate / chart_command / table_command / multi_task /
// dashboard_reset / platform_qa / state_query) into one `<kind>.ground` span. status "error" flags a DEAD-END clarify —
// a question the user can't act on (no target, no options) — which is almost always a grounding failure
// (the empty-nav-catalog class), so it shows red in the waterfall.
export const buildRoutedGroundingSpan = (kind, proposal, utterance) => {
    const p = proposal || {};
    const clar = !!p.needsClarification;
    const options = p.options || [];
    let decision = clar ? "clarify" : "act";
    let status = "ok";
    let output = {};
    switch (kind) {
        case "navigate":
            output = { target: p.target ? { key: p.target.key, label: p.target.label, url: p.target.url } : null, options };
            if (clar && !p.target && !options.length) status = "error"; // couldn't ground to ANY reachable page
            break;
        case "chart_command":
            output = { changes: p.changes || [], options };
            break;
        case "table_command":
            decision = clar ? "clarify" : p.autoApply ? "auto_apply" : "confirm";
            output = { changes: p.changes || [], options };
            break;
        case "multi_task":
            output = { steps: (p.steps || []).map(s => ({ capability: s.capability, target_page: s.target_page, label: s.label })) };
            break;
        case "dashboard_reset":
            decision = "confirm";
            break;
        case "platform_qa": {
            const qa = p.qa || {};
            decision = qa.answered ? "answered" : "refused";
            output = {
                answered: !!qa.answered,
                citations: (qa.citations || []).map(c => c.key),
                deepLinks: (qa.deepLinks || []).map(d => d.label),
                suggestions: (qa.suggestions || []).length,
                // Which cited pages became TAPPABLE (grounded to a real destination) — the difference between
                // "Otto cited a page" and "the user can get there from the answer", which is invisible
                // otherwise: an ungrounded page citation looks identical to a concept one in the trace.
                citationTargets: (qa.citations || []).filter(c => c && c.target).map(c => c.target.url)
            };
            break;
        }
        case "state_query": {
            // A read of the client's own filter state — it always "answers", so the interesting signals are
            // how many filters it reported and whether the summary could be built at all.
            const groups = p.groups || [];
            decision = groups.length ? "reported" : "none_applied";
            output = { groups: groups.map(g => ({ label: g.label, values: (g.names || []).length })), summary: p.summaryText || "" };
            break;
        }
        default:
            break;
    }
    return makeSpan(`${kind}.ground`, {
        input: { utterance },
        output: { decision, message: p.message, ...output },
        status,
        metadata: { decision, needs_clarification: clar }
    });
};

/**
 * Run the full Stage A + Stage B flow for one utterance.
 * Returns { intent, resolution } — intent is the raw FilterIntent, resolution is the
 * per-phrase decision (auto/confirm/clarify) with cross-field suggestions.
 */
export async function buildProposal({
    utterance,
    projectId,
    projectIds,
    buildingIds,
    dataView,
    activeFilters,
    maps,
    userId,
    sessionId,
    history,
    availableFields,
    fieldsMeta,
    appContext,
    navTargets,
    tableController,
    page = "dashboard",
    skipPageChoice = false,
    // A page-supplied option source for fields whose vocabulary is not in the project catalog view (Asset
    // Management). Routed per FIELD via its `optionSource` stamp — see catalog.fetchCandidates — so a mount
    // that omits this is byte-identical to before.
    candidateSource = null,
    // An ALREADY-DECIDED route for this utterance — set by the plan loop from the step's capability
    // (ROUTE_HINT_BY_CAPABILITY), so a confirmed step replays straight into its capability instead of
    // re-entering the classifier (which could change its mind mid-plan). Omitted for typed messages.
    routeHint = null,
    // Progress handlers for the streamed transport — { onMeta, onRoute, onDelta, onReset }. Passed by the
    // chat so it can paint the route and any model-authored prose before the turn completes. DISPLAY-ONLY:
    // the proposal below is always built from the COMPLETE response, so omitting these (a plan replay, a
    // test) changes nothing but the transport. See core/service.interpretUtterance.
    stream = null
}) {
    const active = buildActiveContext(activeFilters, maps);
    // What the USER decided, as opposed to what is merely in effect — read by the planner so a request that
    // builds on a choice the user already made ("i need an excel" after they filtered to Code Compliance)
    // doesn't get that choice planned a second time. See collectUserState / core/decisionLog.
    const userState = await collectUserState(activeFilters, maps, { projectId, projectIds, buildingIds });

    const interpretProjectId =
        projectId != null ? String(projectId) : projectIds && projectIds.length ? String(projectIds[0]) : "";

    const requestId = genRequestId();

    // /interpret returns { request_id, session_id, latency_ms, intent }; unwrap to the FilterIntent.
    const interpretResponse = await interpretUtterance(
        {
            utterance,
            project_id: interpretProjectId,
            user_id: userId || null,
            session_id: sessionId || null,
            data_view: dataView || "project",
            active_filters: active,
            history: history || [],
            available_fields: availableFields || [],
            app_context: appContext || null,
            // The surface the user is on ("dashboard" today; "recommendations" etc. later). Recorded server-side
            // for now; the router will scope the classifier to this page's mounted capabilities in a later step.
            page: page || "dashboard",
            // The user's own decisions (applied filters + what changed since their last message). Omitted
            // (not null) when there is nothing to report, so an unfiltered conversation's payload stays
            // byte-identical to today's; an older backend that doesn't know the field ignores it.
            ...(userState ? { user_state: userState } : {}),
            // A plan step's pre-decided route (see the `routeHint` param above). Omitted (not null) when absent
            // so the payload stays byte-identical to today's for every non-plan message; an OLDER backend that
            // doesn't know the field ignores it (Pydantic drops unknown keys) and classifies normally.
            ...(routeHint ? { route_hint: routeHint } : {}),
            request_id: requestId
        },
        stream
    );

    // Emit one grounding span (attached to this turn's request_id) for a NON-filter path before it returns —
    // the frontend decision the tracing waterfall was missing. Best-effort; filter_query emits its own richer
    // spans below instead.
    const sessionIdOut = interpretResponse && interpretResponse.session_id;
    const emitGround = span => emitClientSpans(requestId, sessionIdOut, [span]);

    // What the server classified this message as. Read here (rather than at the dispatch seam below) because
    // the cross-capability seam needs it too: it stands down for a message already routed to `navigate`.
    // The keys this page can actually offer — the same set buildAvailableFields produces, so probing can
    // never reach a field Otto could not name or apply anyway.
    const offerableKeys = new Set((availableFields || []).map(f => f && f.key).filter(Boolean));

    const routedKind = (interpretResponse && interpretResponse.intent_kind) || null;

    // Cross-capability seam (BEFORE dispatch): a bare container noun ("show me regions") is ambiguous across
    // filters / charts / navigation — each of which really services it. The server classifier had to pick
    // ONE; when the user named no capability verb, we OVERRIDE that guess and CONFIRM which they meant,
    // offering only the capabilities actually viable in this scope (>=2 — or a lone NAVIGATE, the case the
    // classifier's filter-by-default systematically gets wrong for a page-owned noun). Same "ambiguity
    // resolution in deterministic frontend code, not a prompt" principle as the routing demotion.
    // Never for a ROUTE-HINTED replay: a plan step's capability is already decided, so there is nothing to
    // disambiguate — a seam that re-opens the question is the same mid-plan mind-changing the hint prevents.
    const crossCapability = routeHint
        ? null
        : detectCrossCapabilityAmbiguity(utterance, {
              availableFields,
              chartContext: appContext,
              navTargets,
              routedKind,
              page
          });
    if (crossCapability) {
        const proposal = buildCapabilityClarifyProposal(interpretResponse, { utterance }, crossCapability);
        emitGround(
            makeSpan("cross_capability.ground", {
                input: { utterance },
                output: { term: proposal.term, choices: (proposal.choices || []).map(c => c.capability), message: proposal.message },
                metadata: { choices: (proposal.choices || []).length }
            })
        );
        return proposal;
    }

    // Router seam: if the message was classified as a different capability (e.g. chart_command, navigate,
    // platform_qa), hand the post-interpret handling to that capability via the registry. Each grounds +
    // confirms its own way, with no candidate fetch / resolve. platform_qa rides this SAME generic path as
    // navigate — its builder reads the interpret response's `qa` payload and grounds any deep_links against
    // the same `navTargets` handed here (no special-casing). filter_query (+ smalltalk/unsupported) falls
    // through to below.
    const handler = handlerForKind(routedKind);
    if (handler) {
        // Capability-vs-page guard: the kind is real, but its capability isn't MOUNTED on this page (a
        // chart command from a table page — only the dashboard has charts). The kind's own builder would
        // clarify against an empty context ("which chart?" on a chartless page — nothing to Apply, no way
        // forward), so instead explain why it can't happen here and offer the page that can, as a
        // confirm-first navigate+replay plan (or an honest message when no such page is reachable).
        const redirect = buildCapabilityRedirectProposal(routedKind, interpretResponse, { utterance, page, navTargets });
        if (redirect) {
            emitGround(
                makeSpan("capability_redirect.ground", {
                    input: { utterance, page },
                    output: {
                        // redirect_plan  — one candidate page (only one mounts it, or the utterance named one)
                        // redirect_choice— several, each offered WITH its own plan (answerable by tap or typing)
                        // clarify        — nowhere reachable; an honest message about where the action lives
                        decision: redirect.plan ? "redirect_plan" : redirect.redirectChoice ? "redirect_choice" : "clarify",
                        steps: (redirect.steps || []).map(s => ({ capability: s.capability, target_page: s.target_page })),
                        choices: (redirect.choices || []).map(c => c.key),
                        message: redirect.message
                    },
                    metadata: { intent_kind: routedKind, page }
                })
            );
            return redirect;
        }
        // Data-mismatch guard, the redirect's sibling: the capability IS mounted here, but the utterance
        // names ANOTHER page's data ("export the recommendations of florida region" said on Buildings —
        // buildings are not recommendations). Without this the local table handler would export the WRONG
        // records. Never on a route-hinted replay: a plan step's destination was already decided.
        if (!routeHint) {
            const dataRedirect = buildDataRedirectProposal(routedKind, interpretResponse, { utterance, page, navTargets });
            if (dataRedirect) {
                emitGround(
                    makeSpan("data_redirect.ground", {
                        input: { utterance, page },
                        output: {
                            decision: "redirect_plan",
                            steps: (dataRedirect.steps || []).map(s => ({ capability: s.capability, target_page: s.target_page })),
                            message: dataRedirect.message
                        },
                        metadata: { intent_kind: routedKind, page }
                    })
                );
                return dataRedirect;
            }
        }
        // table_command grounds its column VALUES against the page's real options, so it needs the live
        // tableController (async); state_query answers FROM the applied filters, so it gets a thunk over the
        // same summary the removal path uses (a thunk, not the summary itself, so the catalog lookups it does
        // only happen for the one kind that needs them). Awaited here so an async proposal resolves first.
        const proposal = await handler.buildProposal(interpretResponse, {
            utterance,
            appContext,
            navTargets,
            page,
            tableController,
            // The QA chips ground concept citations against the LIVE field set (FTO-11): a browsable
            // dimension offers "Browse <label> options", a numeric wildcard "Set a <label> threshold" —
            // both landing on the FTO-10/12 cards. Predicate passed, not imported (module cycle).
            maps,
            isBrowseable: isControlledVocabulary,
            // The project in scope: platform_qa resolves a cited page's PROJECT-SCOPED route
            // ("/project/projectinfo/:id/settings/category") into a real URL with it (qaTargets.js).
            projectId,
            projectIds,
            getActiveSummary: () => buildActiveSummary(activeFilters, maps, { projectId, projectIds, buildingIds })
        });
        emitGround(buildRoutedGroundingSpan(routedKind, proposal, utterance));
        return proposal;
    }

    const intent = (interpretResponse && interpretResponse.intent) || interpretResponse || {};

    // First step of the interaction: the assistant classifies the message before doing any filter work.
    // Greetings / chit-chat (`smalltalk`) and things it can't do (`unsupported`) extract no filters, so we
    // short-circuit here — no candidate fetch, no resolve, no year grounding, nothing to apply. The
    // assistant just replies with a friendly nudge back to describing the data to filter. This branch is
    // ALSO the safety net for a routed intent_kind with no registered handler (an older bundle vs a newer
    // backend): render the backend's own message from wherever the envelope carries it, and use the canned
    // fallback only when the response truly has none — never discard a real answer (the platform_qa skew bug).
    if (intent.intent_kind && intent.intent_kind !== "filter_query") {
        const proposal = {
            intent,
            smalltalk: true,
            message: backendAssistantMessage(interpretResponse) || SMALLTALK_FALLBACK_MESSAGE,
            resolution: { results: [] },
            yearAdjustment: null,
            sessionId: interpretResponse && interpretResponse.session_id,
            requestId,
            utterance
        };
        emitGround(
            makeSpan("smalltalk.ground", {
                input: { utterance },
                output: { intent_kind: intent.intent_kind, message: proposal.message },
                metadata: { intent_kind: intent.intent_kind }
            })
        );
        return proposal;
    }

    // Catalog scoping is by project only; `active` carries placeholder values (opaque ids on the
    // dashboard), so it's used for interpret context, not for narrowing candidate option fetches.
    const scope = { projectId, projectIds, buildingIds, candidateSource }; // buildingIds = D2 per-user permission scope

    // Pure removal ("remove code compliance", "drop the trade filter"): ground it against the ACTIVE filter
    // set (not the full vocabulary) and auto-apply on a match — its own lightweight proposal, no add-a-filter
    // card / candidate fan-out / feasibility. Mixed (remove + add) and clear_all fall through to the card.
    if (isPureRemoval(intent, utterance)) {
        const proposal = await buildRemovalProposal({
            activeFilters,
            maps,
            scope,
            requestId,
            sessionId: interpretResponse && interpretResponse.session_id,
            utterance
        });
        const matched =
            !!((proposal.removals || []).length ||
                (proposal.clears || []).length ||
                (proposal.clearWildcards || []).length ||
                proposal.clearYear);
        emitGround(
            makeSpan("filter.remove.ground", {
                input: { utterance },
                output: {
                    removals: (proposal.removals || []).map(r => ({ field: r.field, name: r.name })),
                    clears: (proposal.clears || []).map(c => c.label),
                    clearWildcards: (proposal.clearWildcards || []).map(c => (c && c.label) || c),
                    clearYear: !!proposal.clearYear,
                    notApplied: (proposal.notApplied || []).map(n => n.phrase)
                },
                // Nothing the user named was applied -> a no-op removal (the "that filter isn't applied" reply).
                status: matched ? "ok" : "error",
                metadata: { matched }
            })
        );
        return proposal;
    }

    // Page-choice clarify: on the dashboard, a fresh filter query that NAMES the Recommendations page is
    // valid two ways (filter the dashboard, or open Recommendations filtered) — ask which. Runs AFTER the
    // removal check (so removals never ask); skipped when the user already answered ("filter here" re-sends
    // with skipPageChoice) and inside a plan (a plan is a decided sequence). See pageChoice.js.
    if (!skipPageChoice) {
        const pageChoice = detectPageChoice(utterance, intent, { page, navTargets });
        if (pageChoice) {
            const proposal = buildPageChoiceProposal(interpretResponse, { utterance }, pageChoice);
            emitGround(
                makeSpan("page_choice.ground", {
                    input: { utterance },
                    output: {
                        target: proposal.target && proposal.target.label,
                        choices: (proposal.choices || []).map(c => c.kind),
                        message: proposal.message
                    },
                    metadata: { choices: (proposal.choices || []).length }
                })
            );
            return proposal;
        }
    }

    // Client-side tracing: the candidate fetch + apply + year-grounding happen in the browser, so they
    // never reach the server on their own. Collect what each catalog fetch actually did (endpoint /
    // searchKey / count) and emit it as a span attached to THIS trace's request_id — the candidate-fetch
    // count/endpoint is the documented tell for starvation. `traceScope` carries the collector into
    // fetchCandidates; the year fetch uses plain `scope` so it doesn't muddy the catalog.fetch span.
    const clientSpans = [];

    // Span: the SEND-TIME field-availability snapshot (scope keys + per-band load state from
    // AiFilterAssistant). The recurring "Otto didn't offer a filter that's right there" class kept evading
    // diagnosis because no trace recorded WHY a band was absent — a missing/foreign scope key, exhausted
    // retries, or the readiness timeout all looked identical (an interpret prompt with fewer fields).
    // status "error" flags a degraded send so bad turns are queryable server-side.
    if (fieldsMeta) {
        clientSpans.push(
            makeSpan("fields.availability", {
                output: fieldsMeta,
                status: fieldsMeta.degraded ? "error" : "ok",
                metadata: { fields: (availableFields || []).length, degraded: !!fieldsMeta.degraded }
            })
        );
    }

    const catalogFetches = [];
    const traceScope = { ...scope, onCatalogFetch: rec => catalogFetches.push(rec) };
    const catalogStart = nowMs();

    // Resolve items: one per committed common-filter phrase (its single field) and one per ambiguous
    // term. For ambiguous terms we don't trust the model to enumerate the right fields — instead we
    // DATA-GROUND it: probe the model's hints PLUS a baseline of value dimensions and let the fields that
    // actually contain a match decide. This keeps field assignment out of the prompt.
    const draftItems = [];
    (intent.common_filters || []).forEach((cf, i) => {
        const meta = maps.dimMap[cf.dimension];
        if (!meta) return;
        (cf.value_phrases || []).forEach((phrase, j) => {
            // Boolean fields (Yes/No) carry the CONCEPT in the field name, not the value — so the phrase is
            // the field name ("immediate"), which won't match "Yes"/"No". Naming the concept = the positive
            // value; resolve against "yes" (or "no" when the phrase is negated) so it matches the real option.
            const resolvePhrase = meta.isBoolean ? booleanValuePhrase(phrase) : phrase;
            // Routing demotion (see the policy block above): an UNNAMED committed phrase probes EVERY
            // controlled-vocab field — the committed field FIRST, so when only it matches, behavior is
            // identical to before. Named/boolean/refinement commits keep the single-field fast path.
            let metas = [meta];
            let kind = "committed";
            if (shouldDemoteCommitted(cf, meta, utterance)) {
                metas = [meta, ...buildAmbiguousMetas(null, maps, offerableKeys).filter(m => m.id !== meta.id)];
                kind = "demoted";
            }
            draftItems.push({ ref: `c:${i}:${j}`, phrase: resolvePhrase, kind, metas, primary: meta.id });
        });
    });
    (intent.ambiguous_terms || []).forEach((at, i) => {
        const metas = buildAmbiguousMetas(at, maps, offerableKeys);
        if (metas.length) draftItems.push({ ref: `a:${i}`, phrase: at.phrase, kind: "ambiguous", metas });
    });
    // Bare free-text wildcards (description/notes contains) get the SAME probe instead of skipping
    // resolution entirely — the "fall protection" class, where the phrase is really a controlled-vocab
    // option. The original text search stays reachable: it rides along as a text field entry (a choosable
    // "search descriptions" suggestion), and if nothing grounds, applyRoutingDemotion keeps the wildcard.
    (intent.wildcard_filters || []).forEach((w, i) => {
        if (!isBareTextWildcard(w, maps, utterance)) return;
        const metas = buildAmbiguousMetas(null, maps, offerableKeys);
        if (!metas.length) return;
        draftItems.push({ ref: `w:${i}`, phrase: String(w.value), kind: "wildcard", metas, wildcardField: w.field });
    });

    // Fetch candidate options (scoped) per field, then resolve. Candidates come from fetchResolveCandidates
    // (bounded dims -> full vocabulary; high-cardinality -> search + token fallback) so the RESOLVER does
    // the matching instead of a substring prefilter. Per-field failures degrade to empty. For ambiguous
    // items we now only drop a field that returned ZERO options at all (nothing to score) — deciding which
    // populated field actually matches is the resolver's job (by score threshold), not a substring miss.
    // Committed items keep their field even when empty, so a true "no match" is still surfaced.
    const items = await Promise.all(
        draftItems.map(async item => {
            const fieldResults = await Promise.all(
                item.metas.map(async meta => {
                    const kind = meta.value_kind || "id";
                    if (kind === "text" || meta.id === "search") return textFieldEntry(meta.id);
                    let candidates = [];
                    if (meta.inlineOptions && meta.inlineOptions.length) {
                        // Dynamic select/boolean field: options live on the field def, not in a catalog —
                        // resolve against them directly (a /get_list call would be meaningless here).
                        candidates = meta.inlineOptions;
                    } else {
                        try {
                            candidates = await fetchResolveCandidates(meta, item.phrase, traceScope);
                        } catch (err) {
                            console.warn("[FilterAssistant] candidate fetch failed for", meta.id, err);
                            catalogFetches.push({ field: meta.id, status: "error", error: String((err && err.message) || err) });
                        }
                    }
                    // Probe fields with no options at all are dropped (nothing to score) — except a
                    // demoted commit's PRIMARY field, which stays so a true "no match" is still surfaced.
                    const droppable = item.kind !== "committed" && meta.id !== item.primary;
                    if (droppable && candidates.length === 0) return null;
                    return fieldEntryForId(meta, candidates);
                })
            );
            const fields = fieldResults.filter(Boolean);
            // A demoted wildcard keeps its own field as a TEXT entry: the resolver scores it as a real
            // option (method "text"), so "search <field> for the phrase" stays one tap away in the card.
            if (item.kind === "wildcard") fields.push(textFieldEntry(item.wildcardField));
            return { ref: item.ref, phrase: item.phrase, fields };
        })
    );

    // Span: the frontend candidate fetch. `output.fetches` carries per-field endpoint/searchKey/count —
    // the documented first thing to check when "a value is right there but comes back no-match" (starved
    // fetch), which the server-side resolve span (counts only) can't fully explain on its own.
    clientSpans.push(
        makeSpan("catalog.fetch", {
            input: { items: draftItems.map(d => ({ ref: d.ref, phrase: d.phrase, fields: d.metas.map(m => m.id) })) },
            output: { fetches: catalogFetches },
            status: catalogFetches.some(f => f.status === "error") ? "error" : "ok",
            latencyMs: nowMs() - catalogStart,
            metadata: { fields: catalogFetches.length, starved: catalogFetches.filter(f => f.count === 0).length }
        })
    );

    let resolution = { results: [] };
    if (items.length) {
        try {
            // Pass the interpret request_id/session_id so resolve's spans (embeddings, rerank) attach to
            // the SAME trace — the dashboard shows the whole pipeline for one message in one waterfall.
            resolution = await resolvePhrases({
                items,
                request_id: requestId,
                session_id: interpretResponse && interpretResponse.session_id
            });
            // Demoted-wildcard bookkeeping: grounded probes replace their wildcard (the text search stays
            // a choosable suggestion); ungrounded probes vanish and the wildcard applies as interpreted.
            applyRoutingDemotion(intent, resolution);
        } catch (err) {
            console.warn("[FilterAssistant] resolve failed", err);
            // The /resolve POST itself failed (network / 5xx). The backend records nothing in that case,
            // so capture it client-side as an error span — otherwise this failure was invisible to tracing.
            clientSpans.push(makeSpan("resolve.error", { status: "error", error: String((err && err.message) || err) }));
        }
    }

    // Display floor: weak scored matches don't earn a pill (see pruneWeakSuggestions). Runs AFTER the
    // demotion bookkeeping (which inspects the raw resolution) and BEFORE the cascade guard / headline /
    // card, so everything downstream sees — and pays for — only the offers we actually show.
    const beforePrune = (resolution.results || []).map(r => ((r && r.suggestions) || []).length);
    resolution = { ...resolution, results: pruneWeakSuggestions(resolution.results) };
    // Trace what the floor removed. Without this the prune is INVISIBLE: the backend's `resolve` span
    // reports the suggestions it returned, the card renders fewer, and nothing records the delta — which is
    // why a missing pill read as "the resolver never scored it" while triaging FTO-6. Only emitted when the
    // floor actually dropped something, so quiet turns stay quiet.
    const prunedPerResult = (resolution.results || []).map((r, i) => {
        const after = ((r && r.suggestions) || []).length;
        return { ref: (r && r.ref) || String(i), phrase: r && r.phrase, before: beforePrune[i], after };
    });
    const prunedTotal = prunedPerResult.reduce((n, p) => n + Math.max(0, (p.before || 0) - p.after), 0);
    if (prunedTotal > 0) {
        clientSpans.push(
            makeSpan("suggestions.prune", {
                output: { results: prunedPerResult.filter(p => (p.before || 0) > p.after) },
                metadata: { dropped: prunedTotal, base_floor: SUGGESTION_SCORE_FLOOR, fuzzy_floor: FUZZY_SCORE_FLOOR, semantic_floor: SEMANTIC_SCORE_FLOOR }
            })
        );
    }

    // FTO-10 field-mention gate: a phrase that EXACTLY names a browsable field's label gets that field's
    // OWN options as the offer, instead of the word's value-matches across every other field ("filter by
    // category" led with 'system' (Recommendation Type) — trace 1ae06a69, fb 365). Runs AFTER the prune
    // (score-less browse pills would fail the display floor) and BEFORE the cascade guard, so browse pills
    // get real feasibility counts like any other suggestion. Composition per phrase:
    //   - no surviving value-match on another field (see valueConflicts) -> the browse result REPLACES the
    //     value noise (user decision: straight to browse — a clarify with one real answer is noise);
    //   - a genuine conflict ("system" IS a Recommendation Type option at exact/1.0; "compliance" is
    //     CONTAINED in category "Code Compliance") -> browse first, the surviving value result kept below
    //     minus the named field's own now-redundant rows — the combined card is the clarification, both
    //     readings tappable.
    // An empty vocabulary (catalog outage / a project without the field's data) skips the gate for that
    // phrase — today's behavior is the fallback, never a dead end.
    const fieldMentions = detectFieldMentions(intent, maps, isControlledVocabulary, utterance);
    if (fieldMentions.length) {
        // Which signal kept the value reading — `exact` (the word IS the option) or `subset` (the option's
        // name contains it). In the span because "why did the browse card swallow / keep this pill?" is the
        // question these traces get read for, and `method` alone can't answer it (both arrive as fuzzy 1.0).
        const conflictBrief = c => ({ field: c.field, name: c.name, via: c.method === "exact" ? "exact" : "subset" });
        const gateStart = nowMs();
        const grounded = [];
        for (const mention of fieldMentions) {
            // FTO-12 — a numeric/date wildcard mention ("filter by cost"): there are no options to browse,
            // so the offer is a threshold ask — one bare `prompt` wildcard pill per matched field (several
            // cost fields can share the mention; picking one IS the field clarification) plus a threshold
            // result that carries the headline and holds the typed-answer question. The value noise for
            // the phrase is replaced the same way the browse path replaces it; a surviving literal exact
            // value-match on some field keeps its result below (same combined rule).
            if (mention.kind === "wildcard") {
                const results = resolution.results || [];
                const idx = results.findIndex(r => r && r.ref === mention.ref);
                const existing = idx >= 0 ? results[idx] : null;
                const conflicts = valueConflicts(existing, null);
                const thresholdResult = buildThresholdResult(mention.metas, {
                    ref: `t:${grounded.length}`,
                    phrase: mention.phrase
                });
                const present = new Set((intent.wildcard_filters || []).map(w => w && w.field));
                intent.wildcard_filters = [
                    ...(intent.wildcard_filters || []),
                    ...mention.metas.filter(m => !present.has(m.id)).map(buildPromptWildcard)
                ];
                if (conflicts.length && existing) {
                    resolution = { ...resolution, results: [...results.slice(0, idx), thresholdResult, existing, ...results.slice(idx + 1)] };
                } else if (existing) {
                    resolution = { ...resolution, results: [...results.slice(0, idx), thresholdResult, ...results.slice(idx + 1)] };
                } else {
                    resolution = { ...resolution, results: [...results, thresholdResult] };
                }
                intent.needs_clarification = true;
                grounded.push({
                    phrase: mention.phrase,
                    mode: "threshold",
                    fields: mention.metas.map(m => m.id),
                    value_conflicts: conflicts.map(conflictBrief)
                });
                continue;
            }
            const meta = mention.meta;
            let options = [];
            if (meta.inlineOptions && meta.inlineOptions.length) {
                options = meta.inlineOptions;
            } else {
                try {
                    // Full vocabulary straight off the in-memory catalog — deliberately NOT
                    // fetchResolveCandidates, whose MAX_CANDIDATES_PER_FIELD cap would silently drop the
                    // tail of a 200+ option field (sub_system: 218). Browsing must see the true total.
                    // eslint-disable-next-line no-await-in-loop
                    options = await fetchCandidates(meta, "", traceScope);
                } catch (err) {
                    console.warn("[FilterAssistant] browse vocabulary fetch failed for", meta.id, err);
                }
            }
            if (!options.length) continue;
            const results = resolution.results || [];
            const idx = results.findIndex(r => r && r.ref === mention.ref);
            const existing = idx >= 0 ? results[idx] : null;
            const conflicts = valueConflicts(existing, meta.id);
            const browse = buildBrowseResult(meta, options, {
                ref: `${BROWSE_REF_PREFIX}${grounded.length}`,
                phrase: mention.phrase
            });
            if (conflicts.length && existing) {
                // Keep the value reading, minus the named field's own rows (subsumed by the browse pills).
                const kept = { ...existing, suggestions: existing.suggestions.filter(s => s && s.field !== meta.id) };
                resolution = { ...resolution, results: [...results.slice(0, idx), browse, kept, ...results.slice(idx + 1)] };
            } else if (existing) {
                resolution = { ...resolution, results: [...results.slice(0, idx), browse, ...results.slice(idx + 1)] };
            } else {
                resolution = { ...resolution, results: [...results, browse] };
            }
            // The card must ASK (nothing is preselected on a browse card), whatever the interpreter thought.
            intent.needs_clarification = true;
            grounded.push({
                phrase: mention.phrase,
                field: meta.id,
                mode: conflicts.length ? "combined" : "browse",
                options_total: browse.browse.total,
                shown: browse.browse.shown,
                value_conflicts: conflicts.map(conflictBrief)
            });
        }
        if (grounded.length) {
            clientSpans.push(
                makeSpan("field_mention.ground", {
                    input: {
                        mentions: fieldMentions.map(m => ({
                            phrase: m.phrase,
                            fields: m.kind === "wildcard" ? m.metas.map(x => x.id) : [m.meta.id]
                        }))
                    },
                    output: { grounded },
                    latencyMs: nowMs() - gateStart,
                    metadata: { mentions: grounded.length }
                })
            );
        }
    }

    // Cascaded-count guard (see helpers above): with filters already applied, stamp each suggestion with
    // its REAL count under the current filter state and hide/flag the ones that would empty the dashboard.
    // Best-effort — a failed cascade must never block the proposal (degrades to full-vocab behavior).
    //
    // DYNAMIC (inline-option) fields run through the same guard but from a different count source: their
    // options are config-sourced, so a suggestion can be empty even with NO other filter applied (the
    // catalog cascade can't count them at all — `uncounted_dimensions`). fetchDynamicOptionCounts mirrors
    // the FilterModal's own /get_list (faceted by the dashboard's RAW applied params, own field excluded),
    // so the guard validates them whenever they're suggested, not only when a cascade exists.
    const cascade = buildCascade(activeFilters);
    const dynamicMetas = collectDynamicSuggestionMetas(resolution.results, maps);
    if ((cascade || dynamicMetas.length) && (resolution.results || []).length) {
        try {
            const cascadeStart = nowMs();
            // Fire the dynamic /get_list fetches in parallel with the catalog cascade. Conditioning: the
            // dashboard's raw params when provided; a raw-less caller still validates against the project
            // scope alone — safe for hiding (an unconditional 0 is 0 under ANY filters), and identical to
            // today's behavior for everything it can't prove empty.
            const dynamicStatus = [];
            const dynamicPromise = Promise.all(
                dynamicMetas.map(async meta => {
                    const conditionParams = withProjectScope(activeFilters && activeFilters.raw, scope);
                    const res = await fetchDynamicOptionCounts({ meta, conditionParams });
                    dynamicStatus.push({ field: meta.id, status: res ? "ok" : "unverifiable", options: res ? res.list.length : 0 });
                    return { meta, res };
                })
            );
            const counts = cascade ? await fetchCascadedCounts(scope, cascade) : {};
            if (cascade) {
                const suggestedFields = new Set();
                (resolution.results || []).forEach(r =>
                    (r.suggestions || []).forEach(s => {
                        if (s.value_kind !== "text" && s.field !== "search") suggestedFields.add(s.field);
                    })
                );
                // Same-dimension exclusion: on the dashboard, values WITHIN a dimension are OR'd, so a field
                // that is itself filtered must be counted under the OTHER filters only (else adding a second
                // trade always reads 0). Re-count just those fields with their own dimension removed.
                await Promise.all(
                    [...suggestedFields].map(async fieldId => {
                        const glf = glfKey(maps.dimMap[fieldId]);
                        if (!glf || !cascade.dimensions[glf]) return;
                        const rest = { ...cascade.dimensions };
                        delete rest[glf];
                        const partial = Object.keys(rest).length || cascade.year ? { ...cascade, dimensions: rest } : null;
                        const sub = await fetchCascadedCounts(scope, partial);
                        if (sub[glf]) counts[glf] = sub[glf];
                    })
                );
                // Greying/hiding parity when a DYNAMIC filter is already APPLIED on the dashboard: the
                // catalog cascade ignored that arm (uncounted), so the catalog counts above OVER-count and a
                // suggestion that arm empties would never be hidden. Re-count just the suggested catalog
                // fields via the dashboard's own /get_list, conditioned on the RAW applied params (which DO
                // carry the dynamic filter). Success replaces the catalog counts; failure keeps them.
                // Uncountable arms: an applied dynamic filter (outside the fact views), an applied
                // YEAR window (the catalog's year predicate disagrees with the dashboard's real year
                // filter — see feasibility.js), or an applied WILDCARD (`raw.filters` — cost thresholds
                // etc., which the catalog cannot count but /get_list honors via the same JSON param the
                // dashboard sends).
                const raw = activeFilters && activeFilters.raw;
                const appliedUncountable =
                    !!cascade.year ||
                    !!(raw && raw.filters && Object.keys(raw.filters).length) ||
                    Object.keys(cascade.dimensions).some(glf => isGetListCountable(metaByGlf(maps, glf)));
                if (appliedUncountable && raw) {
                    await Promise.all(
                        [...suggestedFields].map(async fieldId => {
                            const meta = maps.dimMap[fieldId];
                            if (!meta || (meta.inlineOptions && meta.inlineOptions.length)) return;
                            // Schema-keyed metas lack the routing flags — use the dashboard twin (same glf).
                            const rMeta = isGetListRecountable(meta) ? meta : recountableMetaByGlf(maps, glfKey(meta));
                            if (!rMeta) return;
                            const res = await fetchDynamicOptionCounts({ meta: rMeta, conditionParams: withProjectScope(raw, scope) });
                            dynamicStatus.push({ field: meta.id, status: res ? "ok" : "unverifiable", options: res ? res.list.length : 0, recount: true });
                            if (res) counts[glfKey(rMeta)] = res.byKey;
                        })
                    );
                }
            }
            // Merge the dynamic counts under their glf (= the field's paramKey) — applyCascadedCounts then
            // treats them exactly like catalog-covered fields (hide zero-count options, demote a zero-count
            // auto to a warned confirm). A failed dynamic fetch simply leaves its field uncovered (kept).
            (await dynamicPromise).forEach(({ meta, res }) => {
                if (res) counts[glfKey(meta)] = res.byKey;
            });
            const hidden = applyCascadedCounts(resolution.results, counts, maps);
            clientSpans.push(
                makeSpan("cascade.count", {
                    output: { hidden, dynamic: dynamicStatus },
                    latencyMs: nowMs() - cascadeStart,
                    metadata: {
                        fields: Object.keys(counts).length,
                        hidden: hidden.reduce((a, h) => a + h.removed, 0),
                        dynamicFields: dynamicMetas.length
                    }
                })
            );
        } catch (err) {
            console.warn("[FilterAssistant] cascaded count check failed", err);
        }
    }

    const yearStart = nowMs();
    const yearAdjustment = await groundYearRange(intent.year_range, intent.year_relative, scope, utterance);
    if (intent.year_range || intent.year_relative || yearAdjustment) {
        // Span: year grounding (clamp/drop against the client's real years). The decision + note are
        // user-facing but were never traced — now the requested vs applied range is on the trace. The span
        // also fires for a year the INTENT never carried (a now-relative phrase the model couldn't express),
        // and `output.source` says which of the three readings won.
        clientSpans.push(
            makeSpan("year.ground", {
                input: { year_range: intent.year_range, year_relative: intent.year_relative, utterance },
                output: yearAdjustment,
                latencyMs: nowMs() - yearStart
            })
        );
    }

    emitClientSpans(requestId, interpretResponse && interpretResponse.session_id, clientSpans);

    return {
        intent,
        resolution,
        yearAdjustment,
        // One short KB definition of a concept this turn touches (or "") — see pickConceptNote.
        conceptNote: pickConceptNote(interpretResponse),
        sessionId: interpretResponse && interpretResponse.session_id,
        requestId,
        utterance
    };
}

// Ground a requested year range against the client's available years (same source as the
// dashboard's native Years filter: maintenance_years.year). Returns null when no year was
// requested or there's nothing to validate against (apply as-is); otherwise a clamp/drop
// decision plus a human-readable note. This is the year-specific instance of the general rule:
// bounded values must be validated against real data before they're applied.
//
// Three sources of a requested span, in precedence order:
//   now-relative  ("this year")     -> read from the UTTERANCE against the clock (parseRelativeYears)
//   plan-relative ("first 3 years") -> intent.year_relative, resolved against the plan's extent
//   absolute      ("2026 to 2028")  -> intent.year_range, clamped
// Now-relative wins because the backend cannot produce it: the interpret model has no clock, so "this year"
// comes back as a plan-relative guess ({anchor:"first", count:1} -> the plan's FIRST year, 2024). The
// utterance is the ground truth for what the user meant, and parseRelativeYears already stands down when the
// user named a real year (then year_range is exact and authoritative).
export async function groundYearRange(yearRange, yearRelative, scope, utterance) {
    const nowRelative = parseRelativeYears(utterance);
    const hasRange = yearRange && (yearRange.start_year != null || yearRange.end_year != null);
    const hasRelative = yearRelative && yearRelative.count;
    if (!hasRange && !hasRelative && !nowRelative) return null;

    let availableYears = [];
    try {
        const opts = await fetchCandidates({ id: "year", get_list_field: ["maintenance_years.year"], value_kind: "value" }, "", scope);
        availableYears = opts.map(o => parseInt(o.id != null ? o.id : o.name, 10)).filter(n => Number.isFinite(n));
    } catch (err) {
        return null; // can't validate -> fall back to applying as requested
    }
    if (!availableYears.length) return null;

    const sorted = [...new Set(availableYears)].sort((a, b) => a - b);
    const minY = sorted[0];
    const maxY = sorted[sorted.length - 1];
    // The client's real years travel with the decision so the card's year pill can offer THOSE years to edit
    // (never a year the project doesn't have) instead of a free-text box.
    const available = { min: minY, max: maxY, years: sorted };
    const span = (a, b) => (a === b ? `${a}` : `${a}–${b}`);

    // Now-relative span ("this year", "the next 3 years") -> resolve against the CLOCK, then validate against
    // the plan. Unlike the plan-relative path this one can legitimately fall outside the data (a 2024-2025
    // plan has no "this year" in 2026), and when it does we say so rather than snapping to the nearest year:
    // answering a different question quietly is worse than applying nothing (grounding principle).
    if (nowRelative) {
        const current = new Date().getFullYear();
        const reqStart = current + nowRelative.startOffset;
        const reqEnd = current + nowRelative.endOffset;
        if (reqEnd < minY || reqStart > maxY) {
            return {
                appliedRange: null,
                dropped: true,
                available,
                source: "now",
                note: `“${nowRelative.phrase}” is ${span(reqStart, reqEnd)}, and this client's plan only covers ${span(minY, maxY)} — so I left the year filter alone.`
            };
        }
        const start = Math.max(reqStart, minY);
        const end = Math.min(reqEnd, maxY);
        const trimmed = start !== reqStart || end !== reqEnd;
        return {
            appliedRange: { start_year: start, end_year: end },
            dropped: false,
            available,
            source: "now",
            note: trimmed
                ? `I read “${nowRelative.phrase}” as ${span(reqStart, reqEnd)} and trimmed it to ${span(start, end)} (this client's data is ${span(minY, maxY)}).`
                : `I read “${nowRelative.phrase}” as ${span(start, end)}.`
        };
    }

    // Plan-relative span ("first/last/next N years") -> resolve against the ACTUAL available years (the model
    // can't know them). e.g. "first 3 years" of 2024..2035 -> 2024-2026.
    if (hasRelative) {
        const count = yearRelative.count;
        let picked;
        if (yearRelative.anchor === "last") {
            picked = sorted.slice(-count);
        } else if (yearRelative.anchor === "next") {
            const current = new Date().getFullYear();
            const future = sorted.filter(y => y >= current);
            picked = (future.length ? future : sorted).slice(0, count);
        } else {
            picked = sorted.slice(0, count); // "first"
        }
        if (!picked.length) return null;
        const start = picked[0];
        const end = picked[picked.length - 1];
        return {
            appliedRange: { start_year: start, end_year: end },
            dropped: false,
            available,
            source: "plan",
            // Future tense: the year pill starts UNSELECTED, so nothing is applied until the user taps it.
            note: `I read that as the ${yearRelative.anchor} ${count} year${count > 1 ? "s" : ""}: ${span(start, end)} (this client's data is ${span(minY, maxY)}).`
        };
    }

    const reqStart = yearRange.start_year != null ? yearRange.start_year : minY;
    const reqEnd = yearRange.end_year != null ? yearRange.end_year : maxY;

    if (reqEnd < minY || reqStart > maxY) {
        return {
            appliedRange: null,
            dropped: true,
            available,
            source: "absolute",
            note: `This client only has data for ${span(minY, maxY)}, so I didn't apply ${span(reqStart, reqEnd)}.`
        };
    }

    const clampedStart = Math.max(reqStart, minY);
    const clampedEnd = Math.min(reqEnd, maxY);
    const startChanged = yearRange.start_year != null && clampedStart !== yearRange.start_year;
    const endChanged = yearRange.end_year != null && clampedEnd !== yearRange.end_year;
    const note =
        startChanged || endChanged
            ? `Adjusted years to ${span(clampedStart, clampedEnd)} (this client's data is ${span(minY, maxY)}).`
            : null;

    return { appliedRange: { start_year: clampedStart, end_year: clampedEnd }, dropped: false, available, source: "absolute", note };
}

/**
 * Build a neutral apply payload from the proposal + the user's accepted selections.
 * The dashboard maps each dimension's get_list_field to its own paramKey to apply. Wildcard
 * (operator/cost) filters apply too, and a free-text field (description/notes) chosen via a
 * clarification applies as a "contains" wildcard. (Scalar toggles are surfaced for display.)
 */
export function buildApplyPayload({ intent, resolution, selections, maps, yearAdjustment, wildcards: wildcardOverride, year: yearOverride }) {
    const dimensions = [];
    const textWildcards = []; // free-text fields (description/notes) picked via clarify -> "contains" wildcards
    (resolution.results || []).forEach(r => {
        // selections[ref] is a single match, or an array for a multi/group result ("florida" -> all
        // Florida regions). Normalize to a list and apply each.
        const sel = selections[r.ref];
        const chosenList = Array.isArray(sel) ? sel : sel ? [sel] : [];
        if (!chosenList.length) return;
        let action = "set";
        if (typeof r.ref === "string" && r.ref.startsWith("c:")) {
            const idx = parseInt(r.ref.split(":")[1], 10);
            action = (intent.common_filters[idx] && intent.common_filters[idx].action) || "set";
        }
        chosenList.forEach(chosen => {
            if (!chosen) return;
            if (chosen.field === "search") return; // global search isn't a column filter here
            if (chosen.value_kind === "text") {
                // a free-text field (description/notes) chosen via a clarification -> apply as a
                // "contains" wildcard (the value is the phrase the user searched for).
                textWildcards.push({ field_id: chosen.field, operator: "contains", value: chosen.name });
                return;
            }
            const meta = maps.dimMap[chosen.field];
            if (!meta) return;
            dimensions.push({
                get_list_field: meta.get_list_field,
                value_kind: meta.value_kind || "id",
                id: chosen.id,
                name: chosen.name,
                action
            });
        });
    });

    // `clears` can target a dimension (-> get_list_field) or a wildcard field (-> field_id).
    const clears = [];
    const clearWildcards = [];
    (intent.clears || []).forEach(id => {
        if (maps.dimMap[id]) clears.push({ get_list_field: maps.dimMap[id].get_list_field });
        else if (maps.wildcardMap[id]) clearWildcards.push(id);
    });

    // Wildcard filters. A wildcard carrying an operator but NO value/range (and not an emptiness check)
    // is how the model expresses "remove the cost filter" — treat it as a clear, not a (broken) filter.
    const EMPTINESS_OPS = ["is_empty", "is_not_empty"];
    const wildcards = [];
    // When the card supplies its own wildcard list (the user's toggled + inline-edited pills), apply ONLY
    // those — an unselected wildcard is intentionally omitted. Without an override (older callers / tests /
    // plan replay) fall back to the interpreter's full wildcard set, so behavior is unchanged there.
    const sourceWildcards = wildcardOverride != null ? wildcardOverride : intent.wildcard_filters || [];
    sourceWildcards.forEach(w => {
        if (!w || !w.field) return;
        const hasValue = w.value != null && w.value !== "";
        const hasRange = w.value_range && (w.value_range.from_value != null || w.value_range.to_value != null);
        if (!hasValue && !hasRange && !EMPTINESS_OPS.includes(w.operator)) {
            if (!clearWildcards.includes(w.field)) clearWildcards.push(w.field);
            return;
        }
        wildcards.push({ field_id: w.field, operator: w.operator, value: w.value, value_range: w.value_range });
    });
    // Text-field picks from clarifications apply as "contains" wildcards alongside the explicit ones.
    textWildcards.forEach(w => wildcards.push(w));

    // Year. Like the wildcards, the card owns the final say: it passes the year pill's state — the user's
    // toggled-on (and possibly edited) range, or null when they left it off — and ONLY that is applied. An
    // absent override (older callers / tests / plan replay) keeps the previous behavior of applying whatever
    // grounding settled on.
    let year;
    if (yearOverride !== undefined) {
        const y = yearOverride;
        year = y && (y.start_year != null || y.end_year != null) ? { start_year: y.start_year, end_year: y.end_year } : null;
    } else if (yearAdjustment) {
        year = yearAdjustment.dropped ? null : yearAdjustment.appliedRange;
    } else {
        const yr = intent.year_range;
        year = yr && (yr.start_year || yr.end_year) ? { start_year: yr.start_year, end_year: yr.end_year } : null;
    }
    // Scalar toggles (e.g. the dashboard's COST / STATIC FCI / Dashboard Source view modes). Passed
    // through for the apply layer to translate into the dashboard's own params; unsupported keys are
    // dropped there with a diagnostic.
    // EXCEPT a RECORD-level scalar (the QA-band filters): the card carries those as opt-in wildcard PILLS, so
    // leaving them here too would apply one the user never selected — the auto-applied filter the pills exist
    // to prevent. When the card DIDN'T supply a wildcard list (a plan step's payload, tests), they stay: then
    // the scalar IS the only carrier.
    const scalars = (intent.scalar_filters || []).filter(
        s => s && s.key && !(wildcardOverride && isRecordScalarFilter(s, maps))
    );
    return { dimensions, clears, clearWildcards, wildcards, clear_all: !!intent.clear_all, year, scalars };
}
